// src/pages/suivi/SendRapportSuivi.js
import React, { useEffect, useMemo, useRef, useState } from "react"; // [SELECTION] + useRef
import { Link, useParams } from "react-router-dom";
import Layout from "../../components/biblio/Layout";
import { API_SUIVI_URL } from "../../helpers/Constants";
import Loading from "../../helpers/Loading";
import { dateFormat, notify } from "../../helpers/Helper";
import { Save, Send, Trash, Pencil, Eye, PlusCircle } from "../../components/Icons";
import DatePicker from "react-date-picker";
import Toggle from "react-toggle";
import Select from 'react-select';
import Quill from '../../components/ReactQuill';
import 'react-quill/dist/quill.snow.css';
import Modal from "../../components/Modal";
import Swal from "sweetalert2";
import UploadPdfDropzone from "../../components/UploadPdfDropzone";
import WorkflowTimeline from "./utils/rapportWorkflowActivities"
import SecureFileViewer from './utils/SecureFileViewer';

import {
  USER_ROLES,
  USER_ROLES_LABEL,
} from "../../helpers/Constants";

import UserStore from "../../store/UserStore";

/** ===================== Helpers généraux ===================== */
const apiBase = (API_SUIVI_URL || "").replace(/\/$/, "");

const STEPS = [
  "Mission",
  "Structure auditée",
  "Adresse",
  "Fichiers",
  "Recommandations",
  "Recapitulatif",
];

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

function getSuiviToken() {
  try {
    const raw = localStorage.getItem("bib2z_token_SUIVI");
    return raw ? raw.replace(/^"(.*)"$/, "$1") : null;
  } catch {
    return null;
  }
}
function authHeaders(extra = {}) {
  const token = getSuiviToken();
  return {
    Accept: "application/json",
    ...(extra || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function toAbsoluteUrl(pathOrFull) {
  pathOrFull = pathOrFull.replace("/telechargements", "/media/download").replace("/api/media/download", "/media/download");
  if (!pathOrFull) return null;
  if (pathOrFull.startsWith("http")) return pathOrFull;
  return `${(API_SUIVI_URL || "").replace(/\/$/, "")}${pathOrFull}`;
}

function toMB(bytes) { return Math.round((bytes / (1024 * 1024)) * 10) / 10; }
function isPdf(f) { return f && f.type === "application/pdf"; }

const MAX_MB_REPORT = 200;      // Rapport
const MAX_MB_OTHERS = 100;      // Synthèse & Annexes

const exportRecapPDF = async (rapportId) => {
  try {
    const response = await fetch(`${apiBase}/tracking-table/download/${rapportId}`, {
      method: 'GET',
      headers: {
        Accept: 'application/octet-stream', // Indique que vous attendez un fichier
        Authorization: `Bearer ${getSuiviToken()}`, // Ajoutez le token si nécessaire
      },
    });

    if (!response.ok) {
      throw new Error(`Erreur ${response.status}: ${response.statusText}`);
    }
    
    // Téléchargez le fichier
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'recapitulatif_rapport_suivi_'+rapportId+'.pdf'); // Nom du fichier à télécharger
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  } catch (error) {
    console.error('Erreur lors de l\'exportation du PDF:', error);
    notify('Erreur', 'Impossible de générer le PDF.', 'error');
  }
};

// helper: true si un champ est vide
const isEmpty = (v) => !v || v === "";

// helper: true si l'entrée est invalide
const isInvalidEntry = (entry) =>
  isEmpty(entry.institution) || isEmpty(entry.structure) || isEmpty(entry.entite);

// IRIs attendus par le backend
const isIri = (v) => typeof v === "string" && (v.includes("/api/") || v.startsWith("/"));

const iri = {
  typeAudit: (id) => (isIri(id) ? id : `/api/type_audits/${id}`),
  audit: (id) => (isIri(id) ? id : `/api/audits/${id}`),
  typeRevue: (id) => (isIri(id) ? id : `/api/type_revues/${id}`),
  structure: (id) => (isIri(id) ? id : `/api/structures/${id}`),
  localite: (id) => (isIri(id) ? id : `/api/localites/${id}`),
  media: (id) => (isIri(id) ? id : `/api/media/${id}`),
  programme: (id) => (isIri(id) ? id : `/api/programmes/${id}`),
  personne: (id) => (isIri(id) ? id : `/api/personnes/${id}`),
  recommandation: (id) => (isIri(id) ? id : `/api/recommandations/${id}`),
  user: (id) => (isIri(id) ? id : `/api/users/${id}`),
};

const idFromIri = (val) => {
  if (!val) return null;
  const parts = String(val).split("/");
  return parts[parts.length - 1] || null;
};

const iriEquals = (a, b) => {
  if (!a || !b) return false;
  const A = String(a), B = String(b);
  return A === B || idFromIri(A) === idFromIri(B);
};

const INITIAL_RECOMMANDATION = {
  constat: "",
  cause: "",
  textesReglementaires: "",
  consequences: "",
  niveauRisque: "",
  recommandations: "",              // compatibilité legacy
  delaiMiseEnOeuvre: null,
  responsableMiseEnOeuvre: null,    // IRI user
  fraisIndumentPercu: 0,
  isFollowed: false,
  isFraisPercuEquitable: false,
  accusedPeople: [{
    id: null,
    lastname: "",
    firstname: "",
    email: "",
    montantIndumentPercu: 0,
  }],                    // utilisé dans le form (au lieu de accusedPeople)
};
/** ===================== Composant principal ===================== */
export function SendRapportSuivi() {
  const { currentId, actionVerb } = useParams(); // récupère l’ID de l’URL /exemple/:id
  const [currentRapport, setCurrentRapport] = useState(null);
  const [justReadOnly, setJustReadOnly] = useState(false);

  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0); // 0: Mission, 1: Structures, 2: Localités, 3: Pièces & Recommandations
  const [lockToStep4, setLockToStep4] = useState(false);

  const [reportFileName, setReportFileName]   = useState("");
  const [annexeFileName, setAnnexeFileName]   = useState("");
  const [abstractFileName, setAbstractFileName] = useState("");
  const [passwordCode, showPasswordCode] = useState(false);
  const [loadingAddPerson, setLoadingAddPerson] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Modal table de suivi 
  const [suiviOpen, setSuiviOpen] = useState(false);
  const [actionsSuivi, setActionsSuivi] = useState([]);
  const [suiviData, setSuiviData] = useState(null);
  const [suiviModalOpen, setSuiviModalOpen] = useState(false);

  // Modal commentaire 
  const [commentOpen, setCommentOpen] = useState(false);
  const [commentSaving, setCommentSaving] = useState(false);
  const [commentDraft, setCommentDraft] = useState({ file: "", fileId: "", fileName: "", content: "" });
  
  const openCommentModal = (reco) => {
    setCommentDraft({ file: "", fileId: "", fileName: "", content: "", recommandation: iri.recommandation(reco) });
    setCommentOpen(true);
  };
  const closeCommentModal = () => setCommentOpen(false);
  
  const canSaveComment = useMemo(
    () => (commentDraft?.content || "").trim().length > 0, // !!commentDraft?.file &&
    [commentDraft]
  );

  const saveComment = async (draft) => {
    try {
      setCommentSaving(true);
      const payload = {
        rapport: `/api/rapports/${currentId}`,
        recommandation: draft.recommandation,
        file: draft.file,
        message: draft.content,
      };

      if(!draft.recommandation) delete payload.recommandation;
      if(!draft.file) delete payload.file;
      
      const res = await fetch(`${apiBase}/comments`, {
        method: "POST",
        headers: { ...authHeaders({ "Content-Type": "application/json" }) },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        notify("Erreur", "Échec de l'enregistrement du commentaire.", "error");
        return;
      }
      notify("Succès", "Commentaire ajouté.", "success");
      setCommentOpen(false);
      updateCurrentRapport();
    } catch {
      notify("Erreur", "Impossible d'enregistrer le commentaire.", "error");
    } finally {
      setCommentSaving(false);
    }
  };

  // -------- Form state --------
  const [form, setForm] = useState({
    code: "",
    // Étape 0
    typeAudit: "", // IRI ex: /api/type_audits/1
    audit: "",     // IRI ex: /api/audits/1
    typeRevue: "", // IRI ex: /api/type_revues/1
    missionName: "",
    // Étape 1
    typeProgramme: "",   
    programme: [],   // IRI structure
    institution: "", // IRI structure
    structure: "",   // IRI structure
    entite: "",      // IRI structure
    // Étape 2
    departement: "",   // IRI localite
    commune: "",       // IRI localite
    arrondissement: "",// IRI localite
    quartier: "",      // IRI localite
    // Étape 3 - Fichiers (IRIs media)
    reportUrl: "",
    annexeUrl: "",
    abstractUrl: "",
  });

  const [withRecommand, setWithRecommand] = useState(true);
  const [recommandations, setRecommandations] = useState([]);

  // viewer PDF
  const [viewerSource, setViewerSource] = useState(null); // string | array
  const [showViewer, setShowViewer] = useState(false);
  
  /** open PDF in modal */
  const readDoc = (url) => {
    const abs = toAbsoluteUrl(url);
    if (!abs) return;
    setViewerSource(abs);
    setShowViewer(true);
  };

  // -------- Options (selects) --------
  const [typeAudits, setTypeAudits] = useState([]);
  const [audits, setAudits] = useState([]);
  const [users, setUsers] = useState([]);
  const [typeRevues, setTypeRevues] = useState([]);

  const [structuresAll, setStructuresAll] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [structures, setStructures] = useState([]);
  const [entites, setEntites] = useState([]);

  const [departements, setDepartements] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [arrondissements, setArrondissements] = useState([]);
  const [quartiers, setQuartiers] = useState([]);

  const [recoDraft, setRecoDraft] = useState(INITIAL_RECOMMANDATION);

  const [localitesRepeater, setLocalitesRepeater] = useState([
    { departement: "", commune: "", arrondissement: "", quartier: "" }
  ]);
  
  const [structuresRepeater, setStructuresRepeater] = useState([
    { institution: "", structure: "", entite: "" }
  ]);

  // --- Éditeur Quill en onglets ---
  const QUILL_TABS = [
    { key: 'constat',              label: 'Constat *' },
    { key: 'cause',                label: 'Causes *' },
    { key: 'consequences',         label: 'Conséquences *' },
    { key: 'textesReglementaires', label: 'Textes réglementaires *' },
    { key: 'recommandations',      label: 'Recommandation(s) *' },
  ];

  const [activeQuillTab, setActiveQuillTab] = useState('constat');

  const [uploading, setUploading] = useState({
    report: false,
    abstract: false,
    annexes: false,
  });
  const [uploadErr, setUploadErr] = useState(null);

  async function uploadMedia(file) {
    const fd = new FormData();
    fd.append("file", file, file.name);

    const headers = authHeaders();
    delete headers["Content-Type"]; // multipart géré par le navigateur

    const res = await fetch(`${apiBase}/media`, {
      method: "POST",
      headers,
      body: fd,
    });

    if (!res.ok) throw new Error(`Upload échoué (HTTP ${res.status})`);
    const json = await res.json();
    // l’API retourne typiquement un champ uri/url/path
    const id = json?.id;
    if (!id) {
      notify("Erreur", "Quelque chose s'est mal passé au cours de l'upload.", "danger");
      throw new Error("L'API n'a pas renvoyé d'URI de fichier");
    }
    return iri.media(id); // ex: "/api/media/123"
  }

  async function onReportChange(e) {
    setUploadErr(null);
    const f = e.target.files?.[0];
    if (!f) return;

    if (!isPdf(f)) return setUploadErr("Seuls les PDF sont acceptés.");
    if (toMB(f.size) > MAX_MB_REPORT)
      return setUploadErr(`Le rapport dépasse ${MAX_MB_REPORT} MB.`);

    try {
      setUploading((u) => ({ ...u, report: true }));
      const uri = await uploadMedia(f);
      setForm((prev) => ({ ...prev, reportUrl: uri }));
      setReportFileName(f.name);
    } catch (err) {
      setUploadErr(err.message);
    } finally {
      setUploading((u) => ({ ...u, report: false }));
    }
  }

  async function onAbstractChange(e) {
    setUploadErr(null);
    const f = e.target.files?.[0];
    if (!f) return;

    if (!isPdf(f)) return setUploadErr("Seuls les PDF sont acceptés.");
    if (toMB(f.size) > MAX_MB_OTHERS)
      return setUploadErr(`La fiche dépasse ${MAX_MB_OTHERS} MB.`);

    try {
      setUploading((u) => ({ ...u, abstract: true }));
      const uri = await uploadMedia(f);
      setForm((prev) => ({ ...prev, abstractUrl: uri }));
      setAbstractFileName(f.name);
    } catch (err) {
      setUploadErr(err.message);
    } finally {
      setUploading((u) => ({ ...u, abstract: false }));
    }
  }

  async function onAnnexesChange(e) {
    setUploadErr(null);
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    for (const f of files) {
      if (!isPdf(f)) return setUploadErr("Seuls les PDF sont acceptés (annexes).");
      if (toMB(f.size) > MAX_MB_OTHERS)
        return setUploadErr(`Une annexe dépasse ${MAX_MB_OTHERS} MB.`);
    }

    try {
      setUploading((u) => ({ ...u, annexes: true }));
      const uploaded = [];
      for (const f of files) {
        const uri = await uploadMedia(f);
        uploaded.push(uri);
      }
      setForm((prev) => ({
        ...prev,
        annexeUrl: [...(Array.isArray(prev.annexeUrl) ? prev.annexeUrl : []), ...uploaded],
      }));
      setAnnexeFileName(
        files.length === 1 ? files[0].name : `${files.length} fichiers`
      );
    } catch (err) {
      setUploadErr(err.message);
    } finally {
      setUploading((u) => ({ ...u, annexes: false }));
    }
  }

  function removeAnnexeAt(index) {
    setForm((prev) => ({
      ...prev,
      annexeUrl: (prev.annexeUrl || []).filter((_, i) => i !== index),
    }));
  }

  // --------- Gestion des recommandations ---------

  // [SELECTION] état de sélection des lignes
  const [selected, setSelected] = useState(new Set()); // Set<number>
  const allSelected = recommandations.length > 0 && selected.size === recommandations.length;
  const someSelected = selected.size > 0 && selected.size < recommandations.length;
  const selectAllRef = useRef(null);

  // modal personnes incriminées
  const [modalOpen, setModalOpen] = useState(false);
  const [personSearch, setPersonSearch] = useState({ lastname: "", firstname: "", email: "" });
  const [personResults, setPersonResults] = useState([]);

  const [editRecoIdx, setEditRecoIdx] = useState(null);
  // clé pour forcer le remount des champs contrôlés (Quill, DatePicker) après reset
  const [formResetKey, setFormResetKey] = useState(0);
  // chargement des données du rapport courant
  const [autorizedRecipients, setAutorizedRecipients] = useState([]);

  useEffect(() => {
    if (!currentId) return;
    if(actionVerb == 'view' || actionVerb == 'read' || actionVerb == 'detail') setJustReadOnly(true);
    setStep(actionVerb == 'read' ? 5 : 4); // temporaire
    setLockToStep4(true);
    updateCurrentRapport();
  }, [currentId]);

  const updateCurrentRapport = async (currentEditIdx = 0) => {
    try {
      const res = await fetch(`${apiBase}/rapports/${currentId}`, {
        headers: { ...authHeaders({ "Content-Type": "application/ld+json" }) },
      });
      if (!res.ok) return;
      const data = await res.json();
      // "retourne des éléments" : on considère des props non vides ou un tableau non vide
      const hasAny =
        (data && Object.keys(data || {}).length > 0) ||
        (Array.isArray(data?.recommandations) && data.recommandations.length > 0);
      if (hasAny) {
        const toPersonnes = (accused = []) =>
          accused?.map(a => ({
            id: iri.personne(a.person?.id),
            firstname: a.person?.firstname,
            lastname: a.person?.lastname,
            email: a.person?.email,
            recommandation: a.recommandation,
            montantIndumentPercu: Number(a.montantIndumentPercu || 0),
          }));

        const recosFlat = (Array.isArray(data?.recommandations) ? data.recommandations : [])
          .map(r => ({
            ...r,
            accusedPeople: toPersonnes(r.accusedPeople || []),
            id: iri.recommandation(r.id),
            responsableMiseEnOeuvre: typeof r.responsableMiseEnOeuvre === 'object' && r.responsableMiseEnOeuvre !== null
              ? r.responsableMiseEnOeuvre.name + "("+r.responsableMiseEnOeuvre.email+")"
              : r.responsableMiseEnOeuvre
          }));
        
        setEditRecoIdx(currentEditIdx)
        setCurrentRapport({ ...data, recommandations: recosFlat });
        setRecommandations(recosFlat);
        setRecoDraft(recosFlat[0] || INITIAL_RECOMMANDATION);
        setForm({
          code: data.code || "",
          audit: data.audit?.title || "",
          typeAudit: data.audit?.type || "",
          typeRevue: data.typeRevue || "",
          missionName: data.mission || "",
          authorizedRecipients: data.authorizedRecipients || [],
        });
        
        setAbstractFileName(data.abstract?.originalName +'.'+ data.abstract?.fileExtension || "");
        setReportFileName(data.report?.originalName +'.'+ data.report?.fileExtension || "");
        setAnnexeFileName(
          Array.isArray(data.annexes) && data.annexes.length > 0
            ? (data.annexes.map((a) => a.originalName +'.'+ a.fileExtension))
            : "_"
        );
        setLocalitesRepeater(
          Array.isArray(data.localiteAudites) && data.localiteAudites.length > 0
            ? data.localiteAudites?.map(l => ({
                departement: l.departement?.title || l.departement || "",
                commune: l.commune?.title || l.commune || "",
                arrondissement: l.arrondissement?.title || l.arrondissement || "",
                quartier: l.quartier?.title || l.quartier || "",
              }))
            : [{ departement: "", commune: "", arrondissement: "", quartier: "" }]
        );
        setStructuresRepeater(
          Array.isArray(data.structureAudites) && data.structureAudites.length > 0
            ? data.structureAudites?.map(s => ({
                institution: s.institution || "",
                structure: s.structure || "",
                entite: s.entite || "",
              }))
            : [{ institution: "", structure: "", entite: "" }]
        );

      }
    } catch {
      
    }
  };
  
  // get pour table de suivi
  useEffect(() => {
    (async () => {
      try {
      if (editRecoIdx === null) return;
      const idxReco = recommandations[editRecoIdx]?.id;
      const resSuivi = await fetch(`${apiBase}/suivi_recommandations?page=1&itemsPerPage=300&recommandation.rapport.id=${currentId}`, {
        headers: { ...authHeaders({ "Content-Type": "application/ld+json" }) },
      });
      if (!resSuivi.ok) return;
      const dataSuivi = await resSuivi.json();
      setActionsSuivi(dataSuivi || []);
    } catch {
      
    }
    })();
  }, [editRecoIdx, suiviOpen]);

  // Pour éditer une recommandation (clone + conversion date)
  const startEditRecommandation = (idx) => {
    const src = recommandations[idx] || {};
    const clone = JSON.parse(JSON.stringify(src));
    clone.delaiMiseEnOeuvre = clone.delaiMiseEnOeuvre ? new Date(clone.delaiMiseEnOeuvre) : null;
    setEditRecoIdx(idx);
    setRecoDraft(clone);
    // ensure controlled editors reflect the loaded values
    setFormResetKey(k => k + 1);
  };

  const validateRecoPayload = () => {
    //  Au moins une personne incriminée non null si frais > 0
    if (Number(recoDraft.fraisIndumentPercu || 0) > 0) {
      const personnesValides = (recoDraft.accusedPeople || []).filter(p => p.id !== null);
      if (personnesValides.length === 0) {
        notify(
          "Erreur",
          "Veuillez ajouter au moins une personne incriminée lorsque des frais indûment perçus sont indiqués.",
          "warning"
        );
        return false;
      }
    }
    
    // --- Validation: somme des montants personnes == frais total ---
    const frais = Number(recoDraft.fraisIndumentPercu || 0);
    let totalPersonnes = 0;
    if (recoDraft.isFraisPercuEquitable && (recoDraft.accusedPeople?.length || 0) > 0) {
      // en mode équitable, le total = frais (par définition)
      totalPersonnes = frais;
    } else {
      totalPersonnes = (recoDraft.accusedPeople || [])
        .map(p => Number(p?.montantIndumentPercu || 0))
        .reduce((a, b) => a + (isNaN(b) ? 0 : b), 0);
    }
    if (Number.isFinite(frais) && Number.isFinite(totalPersonnes)) {
      const equal = Math.abs(frais - totalPersonnes) < 0.000001;
      if (!equal) {
        notify(
          "Erreur",
          "La somme des montants des personnes incriminées doit être égale au total des frais indûment perçus.",
          "warning"
        );
        return false;
      }
    }
    return true;
  };

  // Ajout ou mise à jour de recommandation
  const addOrUpdateRecommandation = (e) => {
    e.preventDefault();
    if(!validateRecoPayload()) return;
    // Préparer l'objet prêt pour le payload
    const ready = {
      ...recoDraft,
      // assurer la présence du champ attendu par le payload
      recommandations: recoDraft.recommandations || "",
      delaiMiseEnOeuvre: recoDraft.delaiMiseEnOeuvre
        ? new Date(recoDraft.delaiMiseEnOeuvre).toISOString()
        : null,
      accusedPeople: recoDraft.accusedPeople || []
    };

    if (editRecoIdx !== null) {
      // Mise à jour
      setRecommandations((prev) =>
        prev.map((r, i) => (i === editRecoIdx ? ready : r))
      );
    } else {
      // Ajout
      setRecommandations((prev) => [...prev, ready]);
    }

    // Reset propre : clone profond + forcer remount des éditeurs contrôlés
    setRecoDraft(JSON.parse(JSON.stringify(INITIAL_RECOMMANDATION)));
    setEditRecoIdx(null);
    setSelected(new Set());
    setFormResetKey(k => k + 1);
    setActiveQuillTab('constat');
  };

  // Confirmation et suppression de recommandation
  const confirmAndRemoveRecommandation = (idx) => {
    // swal2 peut être utilisé ici pour une meilleure UI
    Swal.fire({
			title: "Faut-il vraiment supprimer cette recommandation ?",
			showCancelButton: true,
			confirmButtonColor: "#019264",
			confirmButtonText: "Confirmer",
			cancelButtonText: "Annuler",
		}).then((result) => {
      if (result.isConfirmed) {
        removeRecommandation(idx);
      }
    });
  };

  // Reset du draft de recommandation
  const resetRecoDraft = () => {
    setRecoDraft(JSON.parse(JSON.stringify(INITIAL_RECOMMANDATION)));
    setEditRecoIdx(null);
    setFormResetKey(k => k + 1);
    setActiveQuillTab('constat');
  };

  const openPersonModal = () => setModalOpen(true);
  const closePersonModal = () => { setModalOpen(false); setPersonSearch({ lastname: "", firstname: "", email: "" }); setPersonResults([]); };

  // Autocomplétion API
  const handlePersonSearch = async (field, value) => {
    setPersonSearch(prev => ({ ...prev, [field]: value }));
    if (value.length < 2) return setPersonResults([]);
    try {
      const res = await fetch(`${apiBase}/personnes?${field}=${encodeURIComponent(value)}`, { headers: authHeaders() });
      if (res.ok) setPersonResults(await res.json());
      else setPersonResults([]);
    } catch {
      setPersonResults([]);
    }
  };

  // Ajout personne
  const handleAddPerson = async (person, personIsNew=false) => {
    // Vérification des champs requis pour une nouvelle personne
    if (!person) {
      notify("Erreur", "Données de la personne invalides.", "warning");
      return;
    }

    if (person.email) {
      person.email = person.email.trim().toLowerCase();

      // Validation de l'email
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(person.email)) {
        notify("Erreur", "L'email fourni n'est pas valide.", "danger");
        return;
      }
    }
    else {
      delete person.email; // supprime le champ si vide
    }

    if(personIsNew){
      delete person.id; // s'assure qu'on crée une nouvelle personne
      setLoadingAddPerson(true);
      const res = await fetch(`${apiBase}/personnes`, {
        method: "POST",
        headers: {
          ...authHeaders({ "Content-Type": "application/json", }),
        },
        body: JSON.stringify(person),
      });
      if(res.ok){
        notify("Succès", "La personne a été ajoutée.", "success");
        person = await res.json();
        setLoadingAddPerson(false);
      }
      else{
        notify("Erreur", "Échec de l'ajout de la personne.", "danger");
        setLoadingAddPerson(false);
        return;
      }
    }
    person.id = iri.personne(person.id);
    if(recoDraft?.accusedPeople?.find(p => p.id === person.id)){
      notify("Attention", "Cette personne a déjà été ajoutée.", "warning");
      return;
    }
    // Exclure les entrées "placeholder" dont id === null avant d'ajouter
    setRecoDraft(prev => {
      const next = { 
        ...prev, 
        accusedPeople: [...(prev.accusedPeople || []).filter(p => p.id !== null), person]
      };
      if (next.isFraisPercuEquitable) {
        next.accusedPeople = distributeEquitable(Number(next.fraisIndumentPercu || 0), next.accusedPeople);
      }
      return next;
    });
    closePersonModal();
  }

  // remove personne
  const removePersonne = (idx) => {
    setRecoDraft(prev => {
      let accusedPeople = (prev.accusedPeople || []).filter((_, i) => i !== idx);
      if (prev.isFraisPercuEquitable) {
        accusedPeople = distributeEquitable(Number(prev.fraisIndumentPercu || 0), accusedPeople);
      }
      return { ...prev, accusedPeople };
    });
  };
  
  useEffect(() => {
    setRecoDraft(prev => {
      if (!prev.isFraisPercuEquitable) return prev;
      const n = prev.accusedPeople?.length || 0;
      if (n === 0) return prev;
      const redistributed = distributeEquitable(Number(prev.fraisIndumentPercu || 0), prev.accusedPeople);
      // évite un set en boucle si rien ne change
      const same = redistributed.every((p, i) => Number(p.montantIndumentPercu) === Number(prev.accusedPeople[i].montantIndumentPercu));
      return same ? prev : { ...prev, accusedPeople: redistributed };
    });
  }, [recoDraft.isFraisPercuEquitable, recoDraft.fraisIndumentPercu, recoDraft.accusedPeople?.length]);

  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = someSelected;
  }, [someSelected]);

  const toggleOne = (idx) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => {
      if (recommandations?.length === 0) return new Set();
      if (prev.size === recommandations?.length) return new Set(); // décocher tout
      return new Set(recommandations?.map((_, i) => i));           // cocher tout
    });
  };

  const removeSelected = () => {
    if (selected.size === 0) return;
    setRecommandations((prev) => prev.filter((_, i) => !selected.has(i)));
    setSelected(new Set());
  };
  // [/SELECTION]
  
  // Répartition équitable avec correction d'arrondi (2 décimales)
  function distributeEquitable(total, accusedPeople = []) {
    const n = Array.isArray(accusedPeople) ? accusedPeople.length : 0;
    const T = Number(total || 0);
    if (n <= 0) return accusedPeople;

    const cents = Math.round(T * 100);   // total en "centimes"
    const base = Math.floor(cents / n);  // part minimale
    let rest = cents - base * n;         // centimes restants

    return accusedPeople?.map((p, i) => {
      const add = rest > 0 ? 1 : 0;      // on ajoute 1 centime aux 'rest' premiers
      if (rest > 0) rest -= 1;
      const part = (base + add) / 100;
      return { ...p, montantIndumentPercu: part };
    });
  }

  // ——— Validations Step 1 ———
  const hasAnyStructValue = (r) =>
    !!(r?.institution || r?.structure || r?.entite);

  const isStructRowConsistent = (r) => {
    if (!hasAnyStructValue(r)) return true;                 // ligne vide tolérée
    if (!r.institution && (r.structure || r.entite)) return false; // enfant sans parent
    if (!r.structure && r.entite) return false;             // entité sans structure
    return true;
  };

  const hasAtLeastOneValidStruct = (rows) =>
    Array.isArray(rows) && rows.some((r) => !!r?.institution);

  // ——— Validations Step 2 ———
  const hasAnyLocValue = (r) =>
    !!(r?.departement || r?.commune || r?.arrondissement || r?.quartier);

  const isLocRowConsistent = (r) => {
    if (!hasAnyLocValue(r)) return true;                          // ligne vide tolérée
    if (!r.departement && (r.commune || r.arrondissement || r.quartier)) return false; // enfant sans parent
    if (!r.commune && (r.arrondissement || r.quartier)) return false;                 // arrondissement/quartier sans commune
    if (!r.arrondissement && r.quartier) return false;                                   // quartier sans arrondissement
    return true;
  };

  const hasAtLeastOneValidLoc = (rows) =>
    Array.isArray(rows) && rows.some((r) => !!r?.departement);
 
  // —— Helpers de validation "pleine" pour les repeaters
  const isFilledIri = (v) => isIri(v) && v.trim() !== "";

  // Étape 1 : chaque ligne doit avoir institution, structure, entite
  const isValidStructRow = (r) =>
    r && isFilledIri(r.institution) && isFilledIri(r.structure) && isFilledIri(r.entite);

  // Étape 2 : chaque ligne doit avoir departement, commune, arrondissement, quartier
  const isValidLocRow = (r) =>
    r &&
    isFilledIri(r.departement) &&
    isFilledIri(r.commune) &&
    isFilledIri(r.arrondissement) &&
    isFilledIri(r.quartier);

  const canNext = useMemo(() => {
    if (step === 0) {
      return !!form.typeAudit && !!form.audit && !!form.typeRevue && !!form.missionName;
    }

    if (step === 1) {
      // au moins une ligne ET toutes les lignes entièrement valides
      return Array.isArray(structuresRepeater)
        && structuresRepeater.length > 0
        && structuresRepeater.every(isValidStructRow);
    }

    if (step === 2) {
      // au moins une ligne ET toutes les lignes entièrement valides
      return Array.isArray(localitesRepeater)
        && localitesRepeater.length > 0
        && localitesRepeater.every(isValidLocRow);
    }

    if (step === 3) return !!form.code;
    if (step === 4 && withRecommand) return recommandations?.length > 0;

    return true;
  }, [
    step,
    form.typeAudit,
    form.audit,
    form.typeRevue,
    form.missionName,
    form.code,
    withRecommand,
    recommandations,
    structuresRepeater,
    localitesRepeater,
  ]);

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));
  const mask = (v) => (v ? "•".repeat(Math.min(String(v).length, 10)) : "—");

  function stripAndTruncate(html, maxLength = 120) {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html || "";
    const text = tmp.textContent || tmp.innerText || "";
    return text.length > maxLength ? text.slice(0, maxLength) + "…" : text;
  }

  /** ===================== Chargement des options ===================== */
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        // Type projet/programme
        const r0 = await fetch(`${apiBase}/programmes`, { headers: authHeaders() });
        setProgrammes(r0.ok ? await r0.json() : []);

        // Type audits
        const r1 = await fetch(`${apiBase}/type_audits`, { headers: authHeaders() });
        setTypeAudits(r1.ok ? await r1.json() : []);

        // Audits
        const r2 = await fetch(`${apiBase}/audits`, { headers: authHeaders() });
        setAudits(r2.ok ? await r2.json() : []);

        // Type revues
        const r3 = await fetch(`${apiBase}/type_revues`, { headers: authHeaders() });
        setTypeRevues(r3.ok ? await r3.json() : []);

        // Utilisateurs
        const r4 = await fetch(`${apiBase}/users`, { headers: authHeaders() });
        setUsers(r4.ok ? await r4.json() : []);

        // Structures (arbre)
        const r5 = await fetch(`${apiBase}/structures`, { headers: authHeaders() });
        const st = r5.ok ? await r5.json() : [];
        setStructuresAll(Array.isArray(st) ? st : []);
        setInstitutions((Array.isArray(st) ? st : []).filter((s) => !s.parent));

        // Localités (chargement par type)
        const [rD, ] = await Promise.all([
          fetch(`${apiBase}/localites?type=departement`, { headers: authHeaders() }),
          // fetch(`${apiBase}/localites?type=commune`, { headers: authHeaders() }),
          // fetch(`${apiBase}/localites?type=arrondissement`, { headers: authHeaders() }),
          // fetch(`${apiBase}/localites?type=quartier`, { headers: authHeaders() }),
        ]);
        setDepartements(rD.ok ? await rD.json() : []);
        // setCommunes(rC.ok ? await rC.json() : []);
        // setArrondissements(rA.ok ? await rA.json() : []);
        // setQuartiers(rQ.ok ? await rQ.json() : []);

      } catch (e) {
        notify("Erreur", "Impossible de charger les listes", "danger");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /** ===================== Cascades Structures ===================== */
  useEffect(() => {
    const instId = idFromIri(form.institution);
    if (!instId) { setStructures([]); setEntites([]); return; }
    const node = structuresAll.find((s) => String(s.id) === String(instId));
    setStructures(node?.children || []);
    setEntites([]);
  }, [form.institution, structuresAll]);

  useEffect(() => {
    const strId = idFromIri(form.structure);
    if (!strId) { setEntites([]); return; }
    const node =
      structures.find((s) => String(s.id) === String(strId)) ||
      structuresAll.find((s) => String(s.id) === String(strId));
    setEntites(node?.children || []);
  }, [form.structure, structures, structuresAll]);

  /** ===================== Cascades Localités (génériques) ===================== */
  
  // Ajouter une ligne
  const addRepeaterRow = () => {
    setStructuresRepeater(prev => [...prev, { institution: "", structure: "", entite: "" }]);
  };

  // Supprimer une ligne
  const removeRepeaterRow = (idx) => {
    setStructuresRepeater(prev => prev.filter((_, i) => i !== idx));
  };

  useEffect(() => {
    // Reset descendants pour cohérence (sans changer l'apparence)
    setForm((prev) => ({ ...prev, commune: "", arrondissement: "", quartier: "" }));
  }, [form.departement]);

  useEffect(() => {
    setForm((prev) => ({ ...prev, arrondissement: "", quartier: "" }));
  }, [form.commune]);

  useEffect(() => {
    setForm((prev) => ({ ...prev, quartier: "" }));
  }, [form.arrondissement]);

  // Mappe le type parent vers le type enfant à charger
  const CHILD_TYPE_BY_PARENT = {
    D: 'commune',        // Département -> communes
    C: 'arrondissement', // Commune -> arrondissements
    A: 'quartier',       // Arrondissement -> quartiers
  };

  async function getLocalite(type = 'D', id) {
    id = id.replace( '/api/localites/','')
    const childType = CHILD_TYPE_BY_PARENT[type];
    if (!childType) {
      console.warn(`Type parent inconnu: ${type}`);
      return [];
    }
    if (!id) {
      console.warn('getLocalite: id parent manquant');
      return [];
    }

    // Construire l’URL proprement
    const url = new URL(`${apiBase}/localites/${id}`);
    // url.searchParams.set('type', childType);
    // url.searchParams.set('parentId', id);

    try {
      const res = await fetch(url.toString(), { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      
      if(data?.children?.length){
        // Mettre à jour le bon state en fonction du type enfant
        switch (childType) {
          case 'commune':
            setCommunes(Array.isArray(data?.children) ? data?.children : []);
            break;
          case 'arrondissement':
            setArrondissements(Array.isArray(data?.children) ? data?.children : []);
            break;
          case 'quartier':
            setQuartiers(Array.isArray(data?.children) ? data?.children : []);
            break;
          default:
            // rien
            break;
        }
      }
      return data;
    } catch (err) {
      console.error('getLocalite erreur:', err);
      // vider le state ciblé en cas d’échec
      switch (childType) {
        case 'commune': setCommunes([]); break;
        case 'arrondissement': setArrondissements([]); break;
        case 'quartier': setQuartiers([]); break;
      }
      return [];
    }
  }

  async function getProgrammes(type = 'projet') {
    try {
      const res = await fetch(`${apiBase}/programmes?type=${type}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setProgrammes(Array.isArray(data) ? data : []);
      return data;
    } catch (err) {
      console.error('getProgrammes erreur:', err);
      setProgrammes([]);
      return [];
    }
  }

  /** ===================== Handlers ===================== */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const addPersonne = (e) => {
    e.preventDefault();
  };

  const removeRecommandation = (idx) => {
    setRecommandations((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateRecommandation = (idx, key, val) => {
    setRecommandations((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, [key]: val } : r))
    );
  };

  /** ===================== Filtrages robustes ===================== */
  const auditsFiltered = useMemo(() => {
    // ✅ corrige le filtre: on compare les IDs des IRIs (plus fiable)
    if (!form.typeAudit) return audits;
    return audits.filter((a) => iriEquals(a.type, form.typeAudit));
  }, [audits, form.typeAudit]);

  /** ===================== Payload (aperçu) ===================== */
  const payloadPreview = useMemo(() => {
    const recos = withRecommand
      ? recommandations?.map((r) => {
          const reco = {
            constat: r.constat,
            cause: r.cause,
            textesReglementaires: r.textesReglementaires,
            consequences: r.consequences,
            niveauRisque: r.niveauRisque,
            delaiMiseEnOeuvre: r.delaiMiseEnOeuvre || null,
            responsableMiseEnOeuvre: r.responsableMiseEnOeuvre || null,
            accusedPeople: [r.accusedPeople || []].flat().map((p) => ({
              person: p.id,
              montantIndumentPercu: Number(p.montantIndumentPercu || 0),
            })),
            fraisIndumentPercu: Number(r.fraisIndumentPercu || 0),
            isFollowed: !!r.isFollowed,
            recommandations: r.recommandations || null,
          };

          // Ajouter `id` uniquement s'il est défini
          if (r.id) {
            reco.id = r.id;
          }

          return reco;
        })
      : [];

    return {
      code: form.code || null,
      report: form.reportUrl || null, // temporaire
      annexes: form.annexeUrl || null, // temporaire
      abstract: form.abstractUrl || null, // temporaire
      comments: [],
      programme: form.programme || [],
      typeAudit: form.typeAudit || null,
      audit: form.audit || null,
      typeRevue: form.typeRevue || null,
      mission: form.missionName || null,
      localiteAudites:
        localitesRepeater
          .filter(
            (l) =>
              l.departement || l.commune || l.arrondissement || l.quartier
          )
          .map((l) => ({
            departement: l.departement || null,
            commune: l.commune || null,
            arrondissement: l.arrondissement || null,
            quartier: l.quartier || null,
          })) || [],
      structureAudites: structuresRepeater || [],
      recommandations: recos || [],
    };
  }, [form, recommandations, withRecommand, localitesRepeater, structuresRepeater]);
  /** ===================== Submit ===================== */
  const handleSubmit = async (e) => {
    e.preventDefault();
    // if(!justReadOnly)
    try {
      setLoading(true);
      // Validations minimales
      if (!form.typeAudit || !form.audit || !form.typeRevue || !form.missionName) {
        notify("Attention", "Veuillez compléter l'étape Mission.", "warning");
        setStep(0);
        return;
      }

      // — Étape 1 : programme + structures (repeaters)
      if (
        !Array.isArray(form.programme) ||
        // form.programme.length === 0 ||
        !form.programme?.every(isIri) ||
        !Array.isArray(structuresRepeater) ||
        structuresRepeater.length === 0 ||
        structuresRepeater.some(r => !isIri(r.institution) || !isIri(r.structure) || !isIri(r.entite))
      ) {
        notify("Attention", "Veuillez compléter l'étape Structure.", "warning");
        setStep(1);
        setLoading(false);
        return;
      }

      // — Étape 2 : localités (repeaters)
      if (
        !Array.isArray(localitesRepeater) ||
        localitesRepeater.length === 0 ||
        localitesRepeater.some(r =>
          !isIri(r.departement) || !isIri(r.commune) || !isIri(r.arrondissement) || !isIri(r.quartier)
        )
      ) {
        notify("Attention", "Veuillez compléter l'étape Localités.", "warning");
        setStep(2);
        setLoading(false);
        return;
      }
      
      // upload de fichiers 
      if (!form.reportUrl || !form.abstractUrl || !form.annexeUrl?.length ) {
        notify("Attention", "L'un des champs de Fichers est vide.", "warning");
        setStep(3);
        return;
      }

      // Submit Post form here
      
      const res = await fetch(`${apiBase}/rapports-inspecteur`, {
        method: "POST",
        headers: {
          ...authHeaders({ "Content-Type": "application/json" }),
        },
        body: JSON.stringify(payloadPreview),
      });

      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(`Échec (${res.status}) ${t}`);
      }

      notify("Succès", "Le rapport a été partagé.", "success");
      // Redirection douce
      window.location.href = "/suivi-recommandations";
    } catch (err) {
      notify("Erreur", err.message || "Échec de l'envoi", "danger");
    } finally {
      setLoading(false);
    }

  };

  const patchReco = async(idx, value = true) => {
    if(idx && (UserStore.hasRole(USER_ROLES.ROLE_IGF)|| UserStore.hasRole(USER_ROLES.ROLE_IGM))){
      const currentEditIdx = editRecoIdx;
      const patchPayload = typeof value === 'boolean' ?
       {isFollowed: value}
       : { ...value };
      
      if(typeof value === 'object'){
        if(!validateRecoPayload()) return;
        delete patchPayload['id']; 
        delete patchPayload['comments']; 
        patchPayload['accusedPeople'] = (value.accusedPeople || []).map((p) => ({
          person: p.id,
          montantIndumentPercu: Number(p.montantIndumentPercu || 0),
        }));
      }

      const res = await fetch(`${apiBase}/recommandations/${idFromIri(idx)}`, {
        method: "PATCH",
        headers: {
          ...authHeaders({ "Content-Type": "application/merge-patch+json" }),
        },
        body: JSON.stringify(patchPayload),
      });
      if (res.ok) {
        notify("Succès", "La recommandation a été mise à jour.", "success");
        // if(value) setSuiviOpen(true);
        updateCurrentRapport(currentEditIdx);
        setJustReadOnly(true);
      } else {
        notify("Erreur", "Échec de la mise à jour de la recommandation.", "danger");
      }      
    }
  }

  const patchRepport = async(field) => {
    if(UserStore.hasRole(USER_ROLES.ROLE_IGF) || UserStore.hasRole(USER_ROLES.ROLE_IGM)){
      let role = UserStore.hasRole(USER_ROLES.ROLE_IGF) ? 'igf' : 'igm';
      let patchPayload = { [field]: form [field] };
      
      if(field === 'authorizedRecipients'){
        patchPayload = {'authorizedRecipients' : form[field].map(u => (u.id))};
      }

      if(field === 'recommendations'){
        patchPayload = { [field]: [recoDraft] };
        setForm({ ...form, [field]: [recoDraft] });
      }

      const res = await fetch(`${apiBase}/rapports-${role}/${currentId}`, {
        method: "PATCH",
        headers: {
          ...authHeaders({ "Content-Type": "application/merge-patch+json" }),
        },
        body: JSON.stringify(patchPayload),
      });
      if (res.ok) {
        notify("Succès", "Le rapport a été mise à jour.", "success");
        setCurrentRapport((prev) => ({ ...prev, [field]: form [field] }));
      } else {
        notify("Erreur", "Échec de la mise à jour du rapport.", "danger");
      }      
    }
  }

  const updateRepport = async (action = 'transmit-to-igf') => {
    const rapportId = currentRapport?.id;
    if (!rapportId) {
      notify("Erreur", "ID de rapport manquant.", "danger");
      return;
    }
    try {
      setLoading(true);
      const res = await fetch(`${apiBase}/${action}/${rapportId}`, {
        method: "PATCH",
        headers: {
          ...authHeaders({ "Content-Type": "application/merge-patch+json" }),
        },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(`Échec (${res.status}) ${t}`);
      }
      notify("Succès", "Le rapport a été transféré au suivi des recommandations.", "success");
      // Redirection douce
      window.location.href = "/suivi-recommandations";
    } catch (err) {
      notify("Erreur", err.message || "Échec du transfert", "danger");
    } finally {
      setLoading(false);
    }
  };

  const postSuivi = async (idx) => {
    try {
      setLoading(true);
      suiviData.recommandation = iri.recommandation(idx);
      suiviData.periodicite = suiviData.periodicite + ' ' + suiviData.periodiciteUnit||'mois';
      
      const res = await fetch(`${apiBase}/suivi_recommandations`, {
        method: "POST",
        headers: {
          ...authHeaders({ "Content-Type": "application/json" }),
        },
        body: JSON.stringify(suiviData),
      });
      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(`Échec (${res.status}) ${t}`);
      }
      notify("Succès", "L'étape de suivi a été ajouté.", "success");
      setSuiviOpen(false);
      setSuiviModalOpen(false);
      updateCurrentRapport();
      setSuiviData({ periodicite: '', periodiciteUnit: 'mois', nextDate: '', comments: '' });
    } catch (err) {
      notify("Erreur", err.message || "Échec de l'ajout du suivi", "danger");
    } finally {
      setLoading(false);
    }
  };

  // Handler pour changer une valeur dans une ligne du repeater
  const handleRepeaterChange = (idx, field, value) => {
    setStructuresRepeater(prev =>
      prev.map((row, i) =>
        i === idx
          ? { ...row, [field]: value, ...(field === "institution" ? { structure: "", entite: "" } : field === "structure" ? { entite: "" } : {}) }
          : row
      )
    );
  };

  // Handler pour changer une valeur dans une ligne du repeater
  const handleLocaliteRepeaterChange = (idx, field, value) => {
    setLocalitesRepeater(prev => {
      return prev.map((row, i) => {
        if (i !== idx) return row;
        let update = { ...row, [field]: value };
        if (field === "departement") {
          update.commune = ""; update.arrondissement = ""; update.quartier = "";
        }
        if (field === "commune") {
          update.arrondissement = ""; update.quartier = "";
        }
        if (field === "arrondissement") {
          update.quartier = "";
        }
        return update;
      });
    });

    // Appel asynchrone pour charger les enfants
    (async () => {
      let data;
      if (field === "departement") {
        data = await getLocalite('D', value);
        setLocalitesRepeater(prev =>
          prev.map((row, i) =>
            i === idx ? { ...row, communes: data?.children || [] } : row
          )
        );
      }
      if (field === "commune") {
        data = await getLocalite('C', value);
        setLocalitesRepeater(prev =>
          prev.map((row, i) =>
            i === idx ? { ...row, arrondissements: data?.children || [] } : row
          )
        );
      }
      if (field === "arrondissement") {
        data = await getLocalite('A', value);
        setLocalitesRepeater(prev =>
          prev.map((row, i) =>
            i === idx ? { ...row, quartiers: data?.children || [] } : row
          )
        );
      }
    })();
  };

  const addLocaliteRepeaterRow = () => {
    setLocalitesRepeater(prev => [...prev, { departement: "", commune: "", arrondissement: "", quartier: ""}]);
  };

  const removeLocaliteRepeaterRow = (idx) => {
    setLocalitesRepeater(prev => prev.filter((_, i) => i !== idx));
  };

  /** ===================== Render (apparence inchangée) ===================== */
  return (
    <div className="container-fluid mt-3 mb-4">
      {loading && <Loading />}

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 style={{ textTransform: "uppercase" }}>
          {currentId ? "Recommnadations de rapport du " + new Date(currentRapport?.createdAt).toLocaleDateString() 
          : "Soumettre un rapport"
          }
        </h4>
        <Link to="/suivi-recommandations">
          <button type="button" className="page-title" style={{ border: "none" }}>
            Retour à la liste
          </button>
        </Link>
      </div>

      {/* Header des étapes */}
      <div style={{ position: 'relative' }}>
        <nav className="wizard-header">
          { actionVerb !='read' && STEPS.map((label, i) => (
            <button
              key={label}
              type="button"
              className={`step-tab ${i === step ? "is-active" : ""}`}
              onClick={() => { if (!lockToStep4) setStep(i); }}
              disabled={lockToStep4 ? i !== 4 : i > step}   // désactive les étapes futures et différents de 4
              aria-current={i === step ? "page" : undefined}
            >
              <span className="step-index">{String(i + 1)}</span>
              <span className="step-label">{label}</span>
            </button>
          ))}
        </nav>
        {/* <hr style={{position: 'relative', top: '-41px'}} /> */}
      </div>

      <div className="row" style={{paddingRight: "10px"}}>
      <div className={currentRapport?.rapportWorkflowActivities?.length && actionVerb !='read' ? "col-lg-9 step-card" : "col-lg-12 step-card"}>
        <form onSubmit={handleSubmit}>
          {/* ===================== STEP 1: Mission ===================== */}
          {step === 0 && (
            <div className="row">
              <div className="col-md-6 mb-3">
                <span className="label">Type d’audit *</span>
                <Select
                  name="typeAudit"
                  placeholder="Choisir…"
                  noOptionsMessage={() => "Aucune donnée disponible"}
                  options={typeAudits?.map(p => ({ value: iri.typeAudit(p.id), label: p.title }))}
                  value={form.typeAudit ? { value: form.typeAudit, label: typeAudits.find(i => iri.typeAudit(i.id) === form.typeAudit)?.title || form.typeAudit } : null}
                  onChange={(e) => setForm({ ...form, typeAudit: e?.value, audit: "" })}
                  isSearchable
                />
              </div>

              <div className="col-md-6 mb-3">
                <span className="label">Audit *</span>
                <Select
                  name="audit"
                  placeholder="Choisir…"
                  noOptionsMessage={() => "Aucune donnée disponible"}
                  options={auditsFiltered?.map(p => ({ value: iri.audit(p.id), label: p.title }))}
                  value={form.audit ? { value: form.audit, label: auditsFiltered.find(i => iri.audit(i.id) === form.audit)?.title || form.audit } : null}
                  onChange={(e) => setForm({ ...form, audit: e?.value})}
                  isSearchable
                />
              </div>

              <div className="col-md-6 mb-3">
                <span className="label">Type de revue *</span>
                {justReadOnly ? 
                <select className="form-control" disabled>
                  <option selected>
                    {typeRevues.find(i => iri.typeRevue(i.id) === form.typeRevue)?.title || form.typeRevue }
                  </option>
                </select>
                :
                <Select
                  name="typeRevue"
                  placeholder="Choisir…"
                  noOptionsMessage={() => "Aucune donnée disponible"}
                  options={typeRevues?.map(p => ({ value: iri.typeRevue(p.id), label: p.title }))}
                  value={form.typeRevue ? { value: form.typeRevue, label: typeRevues.find(i => iri.typeRevue(i.id) === form.typeRevue)?.title || form.typeRevue } : null}
                  onChange={(e) => setForm({ ...form, typeRevue: e?.value})}
                  isSearchable
                />
                }
              </div>

              <div className="col-md-6 mb-3">
                <span className="label">Mission *</span>
                <input
                  name="missionName"
                  className="form-control"
                  value={form.missionName}
                  onChange={handleChange}
                  placeholder="Ex. Audit de conformité de ..."
                  required
                />
              </div>
            </div>
          )}

          {/* ===================== STEP 2: Structure ===================== */}
          {step === 1 && (
            <div className="row">
              <div className="col-md-6 mb-3">
                <span className="label">Type</span>
                <select
                  name="programme"
                  className="form-control"
                  value={form.typeProgramme}
                  onChange={(e) => {getProgrammes(e.target.value); setForm({ ...form, typeProgramme: e.target.value, programme: [], })}}
                >
                  <option value="">Choisir…</option>
                  <option value="project">Projet</option>
                  <option value="program">Programme</option>
                </select>
              </div>
              
              <div className="col-md-6 mb-3">
                <span className="label">Programme / Projet</span>
                <Select
                  name="programme"
                  placeholder="Choisir…"
                  noOptionsMessage={() => "Aucune donnée disponible"}
                  options={programmes?.map(p => ({ value: iri.programme(p.id), label: p.title + (p.acronym ? ` (${p.acronym})` : '') }))}
                  value={form?.programme?.map(p => ({ value: p, label: programmes?.find(pr => iri.programme(pr.id) === p)?.title || p }))}
                  onChange={(e) => { setForm({ ...form, programme: e ? e?.map(x => x.value) : [] });}} // gérer le multiple
                  isClearable
                  isSearchable
                  isMulti
                />
              </div>                  

              {/* Repeater pour ajouter plusieurs structures (facultatif) */}
              <div className="repeater-container mb-3">          
                {structuresRepeater?.map((row, idx) => (
                  <div className="repeater-row" key={idx}>
                    <div 
                      className={structuresRepeater.length > 1 ? "row repeater-col repeater-with-btn": "row repeater-col"}
                    >
                      <div className="col-md-4 mb-3">
                        <span className="label">Institution *</span>
                        {/* acronyme entre paranthèse sur valeur */}
                        <Select
                          name="institution"
                          placeholder="Choisir…"
                          noOptionsMessage={() => "Aucune donnée disponible"}
                          options={institutions?.map(p => ({ value: iri.structure(p.id), label: p.title + (p.acronym ? ` (${p.acronym})` : '') }))}
                          value={row.institution ? { value: row.institution, label: institutions.find(i => iri.structure(i.id) === row.institution)?.title || row.institution } : null}
                          onChange={(e) => { handleRepeaterChange(idx, "institution", e.value)}}
                          isSearchable
                        />
                      </div>
                      <div className="col-md-4 mb-3">
                        <span className="label">Structure *</span>
                        {/* Filtrer les structures selon l'institution sélectionnée */}
                        <Select
                          name="structure"
                          placeholder="Choisir…"
                          noOptionsMessage={() => "Aucune donnée disponible"}
                          options={(structuresAll.find(s => iri.structure(s.id) === row.institution)?.children || [])?.map(p => ({ value: iri.structure(p.id), label: p.title + (p.acronym ? ` (${p.acronym})` : '') }))}
                          value={row.structure ? { value: row.structure, label: structuresAll.find(i => iri.structure(i.id) === row.structure)?.title || row.structure } : null}
                          onChange={(e) => { handleRepeaterChange(idx, "structure", e?.value)}}
                          isClearable
                          isSearchable
                        />
                      </div>
                      <div className="col-md-4 mb-3">
                        <span className="label">Entité *</span>
                        {/* Filtrer les entités selon la structure sélectionnée */}
                        <Select
                          name="entite"
                          placeholder="Choisir…"
                          noOptionsMessage={() => "Aucune donnée disponible"}
                          options={(structuresAll.find(s => iri.structure(s.id) === row.structure)?.children || [])?.map(p => ({ value: iri.structure(p.id), label: p.title + (p.acronym ? ` (${p.acronym})` : '') }))}
                          value={row.entite ? { value: row.structure, label: structuresAll.find(i => iri.structure(i.id) === row.structure)?.title || row.structure } : null}
                          onChange={(e) => { handleRepeaterChange(idx, "entite", e.value)}}
                          isClearable
                          isSearchable
                        />
                      </div>
                    </div>

                    {structuresRepeater.length > 1 && (
                      <button type="button" className="btn btn-danger repeater-rm" onClick={() => removeRepeaterRow(idx)}>
                        &times;
                      </button>
                    )}
                  </div>
                ))}

                <div className="mb-2">
                  <button type="button" className="btn btn-secondary btn-sm" onClick={addRepeaterRow}>
                    + Ajouter une ligne
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ===================== STEP 3: Localisation ===================== */}
          {step === 2 && (
            <div className="row repeater-container">
              {localitesRepeater?.map((row, idx) => (
                <div className="repeater-row" key={'l-'+idx}>
                  <div className="row repeater-col">
                    <div 
                      className={localitesRepeater.length > 1 ? "row repeater-col repeater-with-btn": "row repeater-col"}
                    >
                      <div className="col-md-3 mb-3">
                        <span className="label">Département *</span>
                        <Select
                          name="departement"
                          placeholder="Choisir…"
                          noOptionsMessage={() => "Aucune donnée disponible"}
                          options={departements?.map(p => ({ value: iri.localite(p.id), label: p.title }))}
                          value={row.departement ? { value: row.departement, label: departements.find(i => iri.localite(i.id) === row.departement)?.title || row.departement } : null}
                          onChange={(e) => handleLocaliteRepeaterChange(idx, "departement", e?.value)}
                          isSearchable
                        />
                      </div>
                      <div className="col-md-3 mb-3">
                        <span className="label">Commune *</span>
                        {!row?.departement ? 
                        <select
                          name="commune"
                          className="form-control"
                          value={row.commune}
                          disabled // désactivé si pas de département
                        >
                          <option value="">Choisir…</option>
                        </select>
                        :
                        <Select
                          name="commune"
                          placeholder="Choisir…"
                          noOptionsMessage={() => "Aucune donnée disponible"}
                          options={localitesRepeater[idx]?.communes?.map(p => ({ value: iri.localite(p.id), label: p.title }))}
                          value={row.commune ? { value: row.commune, label: localitesRepeater[idx]?.communes?.find(i => iri.localite(i.id) === row.commune)?.title || row.commune } : null}
                          onChange={(e) => handleLocaliteRepeaterChange(idx, "commune", e?.value)}
                          disabled={!row?.departement}
                          isSearchable
                        />}
                      </div>
                      <div className="col-md-3 mb-3">
                        <span className="label">Arrondissement *</span>
                        {!row?.commune ? 
                        <select
                          name="arrondissement"
                          className="form-control"
                          disabled // désactivé si pas de commune
                        >
                          <option value="">Choisir…</option>
                        </select>
                        :
                        <Select
                          name="arrondissement"
                          placeholder="Choisir…"
                          noOptionsMessage={() => "Aucune donnée disponible"}
                          options={localitesRepeater[idx]?.arrondissements?.map(p => ({ value: iri.localite(p.id), label: p.title }))}
                          value={row.arrondissement ? { value: row.arrondissement, label: localitesRepeater[idx]?.arrondissements?.find(i => iri.localite(i.id) === row.arrondissement)?.title || row.arrondissement } : null}
                          onChange={(e) => handleLocaliteRepeaterChange(idx, "arrondissement", e?.value)}
                          isSearchable
                        />}
                      </div>
                      <div className="col-md-3 mb-3">
                        <span className="label">Quartier / Village *</span>
                        {!row?.arrondissement ? 
                        <select
                          name="quartier"
                          className="form-control"
                          disabled // désactivé si pas d'arrondissement
                        >
                          <option value="">Choisir…</option>
                        </select>
                        :
                        <Select
                          name="quartier"
                          placeholder="Choisir…"
                          noOptionsMessage={() => "Aucune donnée disponible"}
                          options={localitesRepeater[idx]?.quartiers?.map(p => ({ value: iri.localite(p.id), label: p.title }))}
                          value={row.quartier ? { value: row.quartier, label: localitesRepeater[idx]?.quartiers?.find(i => iri.localite(i.id) === row.quartier)?.title || row.quartier } : null}
                          onChange={(e) => handleLocaliteRepeaterChange(idx, "quartier", e?.value)}
                          isSearchable
                        />}
                      </div>
                    </div>
                  </div>
                  {localitesRepeater.length > 1 && (
                    <button type="button" className="btn btn-danger repeater-rm" onClick={() => removeLocaliteRepeaterRow(idx)}>
                      &times;
                    </button>
                  )}
                </div>
              ))}
              <div className="mb-2">
                <button type="button" className="btn btn-secondary btn-sm" onClick={addLocaliteRepeaterRow}>
                  + Ajouter une localisation
                </button>
              </div>
            </div>
          )}

          {/* ÉTAPE 4 : Fichier jointe */}
          {step === 3 && (
            <div className="row">
              {/* Rapport */}
              <div className="col-12">
                <UploadPdfDropzone
                  label="Rapport de mission (Sans les annexes) *"
                  hint="Format accepté PDF (max. 200MB)"
                  maxMB={200}
                  required={!form.reportUrl}
                  multiple={false}
                  value={
                    form.reportUrl
                      ? { id: form.reportUrlId, name: form.reportFileName }
                      : null
                  }
                  apiBase={apiBase}
                  authHeaders={authHeaders}
                  onUploaded={(fileObjOrNull) => {
                    // fileObjOrNull = { id: "/media/xyz", name: "rapport_final.pdf" } OU null si suppression
                    if (!fileObjOrNull) {
                      setForm(p => ({
                        ...p,
                        reportUrl: "",
                        reportUrlId: "",
                        reportFileName: ""
                      }));
                      return;
                    }

                    setForm(p => ({
                      ...p,
                      reportUrl: iri.media(fileObjOrNull.id), // l'IRI finale
                      reportUrlId: fileObjOrNull.id,         // on garde l'id brut si besoin plus tard
                      reportFileName: fileObjOrNull.name,    // affichage humain
                    }));
                    setReportFileName(fileObjOrNull.name || '');
                  }}
                />
              </div>
              
              {/* Fiche de synthèse */}
              <div className="col-12">
                <UploadPdfDropzone
                  label="Fiche de synthèse "
                  hint="Format accepté PDF (max. 100MB)"
                  maxMB={100}
                  required={!form.abstractUrl}
                  multiple={false}
                  value={
                    form.abstractUrl
                      ? { id: form.abstractUrlId, name: form.abstractFileName }
                      : null
                  }
                  apiBase={apiBase}
                  authHeaders={authHeaders}
                  onUploaded={(fileObjOrNull) => {
                    // fileObjOrNull = { id: "/media/xyz", name: "rapport_final.pdf" } OU null si suppression
                    if (!fileObjOrNull) {
                      setForm(p => ({
                        ...p,
                        abstractUrl: "",
                        abstractUrlId: "",
                        abstractFileName: ""
                      }));
                      return;
                    }

                    setForm(p => ({
                      ...p,
                      abstractUrl: iri.media(fileObjOrNull.id), // l'IRI finale
                      abstractUrlId: fileObjOrNull.id,         // on garde l'id brut si besoin plus tard
                      abstractFileName: fileObjOrNull.name,    // affichage humain
                    }));
                    setAbstractFileName(fileObjOrNull.name || '');
                  }}
                />
              </div>

              {/* Annexes */}
              <div className="col-12">
                <UploadPdfDropzone
                  label="Annexes"
                  hint="Format accepté PDF (max. 100MB)"
                  maxMB={100}
                  required={!(form.annexeUrl && form.annexeUrl.length)}
                  multiple={true}
                  value={form.annexeList /* tableau d'objets {id,name} */}
                  apiBase={apiBase}
                  authHeaders={authHeaders}
                  onUploaded={(finalList) => {
                    setForm(p => ({
                      ...p,
                      annexeList: finalList, // garde {id,name} pour l'UI
                      annexeUrl: finalList?.map(f => iri.media(f.id)),     // ex: pour ton backend
                      // annexeNames: finalList.map(f => f.name),         // si tu veux les noms
                    }));
                    setAnnexeFileName(p => ({
                      ...finalList?.map(f => f.name)         // si tu veux les noms
                    }));
                  }}
                />
              </div>

              {/* Code */}
              <div className="col-md-12 mb-3">
                <span className="label">Code *</span>
                <div className="input-group">
                  <input
                    name="code"
                    type={passwordCode ? "text" : "password"}
                    className="form-control"
                    value={form.code}
                    onChange={handleChange}
                    required
                    placeholder="Entrer le code du rapport"
                  />
                  <div className="input-group-append">
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => showPasswordCode(!passwordCode)}
                      aria-label={passwordCode ? "Masquer" : "Afficher"}
                    >
                      <i className={`fa ${passwordCode ? "fa-eye-slash" : "fa-eye"}`} />
                    </button>
                  </div>
                </div>
              </div>

              {uploadErr && (
                <div className="col-12">
                  <div className="alert alert-warning py-2">{uploadErr}</div>
                </div>
              )}
            </div>
          )}

          {/* ÉTAPE 5 : Ajout recommandation (repeater) */}
          {step === 4 && (
            <>
              {/* Form de saisie d’une nouvelle reco */}
              <div className="bg-light p-3 mt-2">
                <div className="">
                  <div className="nice-border row">
                  <div className="col-md-6 mb-2">
                    <span className="label">Niveau de risque *</span>
                    <select
                      name="niveauRisque"
                      disabled={justReadOnly}
                      className="form-control"
                      value={recoDraft.niveauRisque}
                      onChange={(e) => setRecoDraft({ ...recoDraft, niveauRisque: e.target.value })}
                    >
                      <option value="">Choisir…</option>
                      <option value="Faible">Faible</option>
                      <option value="Moyen">Moyen</option>
                      <option value="Élevé">Élevé</option>
                    </select>
                  </div>

                  <div className="col-md-6 mb-2">
                    <span className="label">Responsable de la mise en œuvre</span>
                    {justReadOnly ? 
                    <select className="form-control" disabled>
                      <option selected>
                        {users.find(i => iri.user(i.id) === recoDraft.responsableMiseEnOeuvre)?.name || recoDraft.responsableMiseEnOeuvre || '' }
                      </option>
                    </select>
                    :
                    <Select
                      name="responsableMiseEnOeuvre"
                      placeholder="Choisir…"
                      noOptionsMessage={() => "Aucune donnée disponible"}
                      options={users?.map(p => ({ 
                        value: iri.user(p.id), 
                        label:  `${p.name || ''} (${p.email || 'Email inconnu'})` 
                      }))}
                      value={recoDraft.responsableMiseEnOeuvre ? { value: recoDraft.responsableMiseEnOeuvre, label: users.find(i => iri.user(i.id) === recoDraft.responsableMiseEnOeuvre)?.name || recoDraft.responsableMiseEnOeuvre } : null}
                      onChange={(e) => setRecoDraft({ ...recoDraft, responsableMiseEnOeuvre: iri.user(e?.value) })}
                      isSearchable
                    />}
                  </div>

                  <div className="col-md-6 mb-2">
                    <span className="label">Échéance / délai de mise en œuvre *</span>
                    <DatePicker
                      disabled={justReadOnly}
                      className="form-control"
                      value={recoDraft.delaiMiseEnOeuvre}
                      key={`date-${formResetKey}`}
                      onChange={(v) => {
                        const now = new Date(); // Date actuelle
                        const selectedDate = new Date(v); // Convertir la valeur sélectionnée en objet Date

                        if (selectedDate < now) {
                          notify("Attention", "La date ne peut pas être antérieure à aujourd'hui.", "warning");
                          setRecoDraft({ ...recoDraft, delaiMiseEnOeuvre: now }); // Utiliser la date actuelle
                        } else {
                          setRecoDraft({ ...recoDraft, delaiMiseEnOeuvre: selectedDate }); // Utiliser la date sélectionnée
                        }
                      }}
                      format="dd/MM/y"
                      minDate={new Date()} // Désactive les dates antérieures à aujourd'hui
                    />
                  </div>

                  <div className="col-md-6 mb-2">
                    <span className="label">Total des frais indûment perçus (FCFA)</span>
                    <input
                      type="number"
                      disabled={justReadOnly}
                      className="form-control"
                      value={recoDraft.fraisIndumentPercu}
                      onChange={(e) => {
                        const nextTotal = Number(e.target.value || 0);
                        setRecoDraft(prev => {
                          if (prev.isFraisPercuEquitable && (prev.accusedPeople?.length || 0) > 0) {
                            const accusedPeople = distributeEquitable(nextTotal, prev.accusedPeople);
                            return { ...prev, fraisIndumentPercu: nextTotal, accusedPeople };
                          }
                          return { ...prev, fraisIndumentPercu: nextTotal };
                        });
                      }}
                    />
                  </div>
                  </div>

                  <div className="repeater-container mb-2 nice-border">
                    <span className="label" style={{ position: 'relative', top: '-20px', backgroundColor: '#f8f9fa',width: 'fit-content', padding: '0 10px' }}>
                      Personne(s) incriminée(s) </span>
                    <div className="col-md-12 mb-2 mr-3 mt-2 d-flex align-items-center" style={{justifyContent: "space-between"}}>
                      <span></span>
                      {!justReadOnly && <>
                        <div className=" d-flex align-items-right" >
                          <Toggle
                            checked={!!recoDraft.isFraisPercuEquitable}
                            onChange={() => {
                              setRecoDraft(prev => {
                                const next = { ...prev, isFraisPercuEquitable: !prev.isFraisPercuEquitable };
                                if (next.isFraisPercuEquitable) {
                                  next.accusedPeople = distributeEquitable(Number(next.fraisIndumentPercu || 0), next.accusedPeople || []);
                                }
                                return next;
                              });
                            }}
                          />
                          <span className="ml-2" style={{marginLeft: "10px"}}>Répartition équitable des frais</span>
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          onClick={openPersonModal}
                          style={{ float: "right" }}
                        >
                          Ajouter une personne
                        </button>
                      </>}
                    </div>
                    {recoDraft?.accusedPeople?.map((p, idx) => (
                    <div className="row repeater-col" key={`person-${idx}`}>
                      <div className={recoDraft.accusedPeople.length > 1 && justReadOnly ? "row repeater-col repeater-with-btn": "row repeater-col"}>
                        <div className="row repeater-col" onClick={() => { if(p.id === null) openPersonModal(idx); }}>
                          <div className="col-md-3 mb-2">
                            <span className="label">Frais perçus *</span>
                            <input
                              className="form-control"
                              type="number"
                              disabled={justReadOnly || p.id === null}
                              value={
                                recoDraft.isFraisPercuEquitable
                                  ? (Number(p.montantIndumentPercu || 0)).toFixed(2)
                                  : (p.montantIndumentPercu ?? 0)
                              }
                              readOnly={recoDraft.isFraisPercuEquitable == true}
                              onChange={(e) => {
                                const updatedPersonnes = recoDraft.accusedPeople?.map((person, i) =>
                                  i === idx ? { ...person, montantIndumentPercu: e.target.value } : person
                                );
                                setRecoDraft({ ...recoDraft, accusedPeople: updatedPersonnes });
                              }}
                              placeholder=""
                            />
                          </div>
                          <div className="col-md-3 mb-2">
                            <span className="label">Nom *</span>
                            <input
                              className="form-control"
                              disabled={justReadOnly}
                              onClick={() => { 
                                if(p.id !== null) openPersonModal(idx); 
                                setPersonSearch(p);
                                document.getElementById('personLastname').focus();
                              }}
                              type="text"
                              readOnly
                              value={p.lastname || ""}
                              name="lastname"
                              placeholder=""
                            />
                          </div>
                          <div className="col-md-3 mb-2">
                            <span className="label">Prénom *</span>
                            <input
                              className="form-control"
                              disabled={justReadOnly}
                              onClick={() => { 
                                if(p.id !== null) openPersonModal(idx);
                                setPersonSearch(p);
                                document.getElementById('personFirstname').focus();
                              }}
                              type="text"
                              readOnly
                              value={p.firstname || ""}
                              name="firstname"
                              placeholder=""
                            />
                          </div>
                          <div className="col-md-3 mb-2">
                            <span className="label">Email </span>
                            <input
                              className="form-control"
                              disabled={justReadOnly}
                              onClick={() => { 
                                if(p.id !== null) openPersonModal(idx);
                                setPersonSearch(p);
                                document.getElementById('personEmail').focus();
                              }}
                              type="email"
                              readOnly
                              value={p.email || ""}
                              name="email"
                              placeholder=""
                            />
                          </div>
                        </div>
                      </div>
                      { !justReadOnly && recoDraft.accusedPeople.length > 1 && (
                        <button type="button" className="btn btn-danger repeater-rm" style={{position: 'relative', right: '12px'}} onClick={() => removePersonne(idx)}>
                          &times;
                        </button>
                      )}
                    </div>
                    ))}
                  </div>

                  {/* === Éditeur en onglets (remplace les 5 Quill) === */}
                  <div className="col-md-12 mb-2 mt-3 p-2">
                    <ul className="nav nav-tabs mb-2 ">
                      {QUILL_TABS.map(tab => (
                        <li className="nav-item" key={tab.key}>
                          <button
                            type="button"
                            className={`nav-link ${activeQuillTab === tab.key ? 'active' : ''}`}
                            onClick={() => setActiveQuillTab(tab.key)}
                          >
                            {tab.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                    <Quill
                      theme="snow"
                      readOnly={justReadOnly}
                      className={justReadOnly ? "quill-readonly":""}
                      key={`${activeQuillTab}-${formResetKey}`}
                      title={QUILL_TABS.find(t => t.key === activeQuillTab)?.label}
                      value={recoDraft?.[activeQuillTab] || ""}
                      onChange={(val) =>
                        setRecoDraft(prev => ({ ...prev, [activeQuillTab]: val }))
                      }
                    />
                  </div>
                  
                  {/* Du ressort de l'IGF uniquement */}
                  {UserStore.hasRole(USER_ROLES.ROLE_IGF) &&
                  <div className="row" style={{position:'relative'}}>
                    <div className="col-md-11 mb-2 mt-3">
                      <span className="label">Partager le rapport avec...</span>
                      
                      <Select
                        name="authorizedRecipients"
                        placeholder="Choisir…"
                        noOptionsMessage={() => "Aucune donnée disponible"}                        
                        options={users?.map(u => ({ 
                          value: iri.user(u.id), 
                          label: `${u.name || ''} (${u.email || 'Email inconnu'})` 
                        })) || []}
                        value={form.authorizedRecipients?.map(u => ({label: u.name , value: iri.user(u.id)})) || []}
                        onChange={(e) => {
                          const selectedUsers = e ? e.map(sel => {
                            const userObj = users.find(u => iri.user(u.id) === sel.value);
                            return { id: sel.value, name: userObj ? userObj.name : sel.value };
                          }) : [];
                          setForm(p => ({ ...p, authorizedRecipients: selectedUsers }));
                        }}
                        isClearable
                        isSearchable
                        isMulti
                      />
                    </div>
                    <div className="col-md-1" style={{position: "absolute", bottom:'0px', right: '0px', margin: '10px 20px'}}>
                      <button
                        type="button"
                        className="btn btn-secondary mx-2"
                        onClick={()=>patchRepport('authorizedRecipients')}
                        disabled={loading}
                      >
                        {loading ? "..." : "Partager"}
                      </button>
                    </div>
                  </div>
                  }
                  {/* Fin Du ressort de l'IGF uniquement */}
                  
                  {!justReadOnly && actionVerb != 'detail'  && <div className="col-md-12 mb-2 mr-3 mt-3 d-flex align-items-center">
                    
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={addOrUpdateRecommandation}
                      disabled={
                        !recoDraft.constat ||
                        !recoDraft.recommandations ||
                        !recoDraft.niveauRisque ||
                        !recoDraft.delaiMiseEnOeuvre
                      }
                      style={{ float: "right" }}
                    >
                      <Save /> {editRecoIdx !== null ? "Mettre à jour la recommandation" : "Ajouter la recommandation"}
                    </button>
                    {editRecoIdx !== null && (
                      <button
                        type="button"
                        className="btn btn-sm btn-light mx-4"
                        onClick={resetRecoDraft}
                        style={{ float: "right", marginLeft: "20px" }}
                      >
                        Annuler la modification
                      </button>
                    )}
                  </div>}
                </div>
              </div>
              
            </>
          )}
          
          {/* ÉTAPE 6 : Récap + Submit */}
          {step === 5 && (<div id="printable-recap-section">
            {/* IGF uniquement */}
            {(UserStore.hasRole(USER_ROLES.ROLE_IGF) || UserStore.hasRole(USER_ROLES.ROLE_IGM)) && actionVerb === "detail" && <div className="col-12 mb-4" style={{display: "flex", alignItems: "center", gap: "20px", justifyContent: "space-between", position: 'relative'}}>
              <img src="/img/icon-igf.png" alt="IGF" style={{ maxHeight: "80px" }} />
              <h3>Fiche de suivi des recommandations</h3>
              <span title="Fermer" style={{cursor: "pointer", position: 'absolute', top: -10, right: -5, fontWeight: 700, fontSize: 15}} onClick={() => setStep(4)}>X</span>
            </div>}
            <div className="row row-cols-1 row-cols-md-2 g-2">
              
              {/* Recap Mission */}
              <div className="col" onClick={() => {if(actionVerb == 'add') setStep(0)}} title={(actionVerb == 'add') ? "Cliquez pour modifier les détails de la mission": ""} style={{cursor: "pointer"}}>
                <div className="card compact-card">
                  <div className="card-body recap">
                    <div className="section-title recap-title">Mission</div>
                    <div className="row g-2">
                      <div className="col-6">
                        <strong>Type d’audit</strong>
                        <div>{typeAudits?.find(t => iri.typeAudit(t.id) === form.typeAudit)?.title || form.typeAudit || "—"}</div>
                      </div>
                      <div className="col-6">
                        <strong>Audit</strong>
                        <div>{audits?.find(a => iri.audit(a.id) === form.audit)?.title || form.audit || "—"}</div>
                      </div>
                      <div className="col-6">
                        <strong>Type de revue</strong>
                        <div>{typeRevues?.find(t => iri.typeRevue(t.id) === form.typeRevue)?.title || form.typeRevue || "—"}</div>
                      </div>
                      <div className="col-6">
                        <strong>Mission</strong>
                        <div>{form.missionName || "—"}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recap Structure auditée */}
              <div className="col" onClick={() => {if(actionVerb == 'add') setStep(1)}} title={(actionVerb == 'add') ? "Cliquez pour modifier les structures auditées":""} style={{cursor: "pointer"}}>
                <div className="card compact-card">
                  <div className="card-body recap">
                    <div className="section-title recap-title">Structure{structuresRepeater.length>1 ? 's' : ''} auditée{structuresRepeater.length>1 ? 's' : ''}</div>
                    {structuresRepeater.filter(iri => iri.institution || iri.structure || iri.entite).length > 0 ? (
                    structuresRepeater?.map((row, i) => (
                      <div key={'str'+i} className="mb-2">
                        {structuresRepeater.length > 1 && <><strong>Structure {i + 1}:</strong>{" "}</>}
                        {(() => {
                          const parts = [
                            <div key="inst"><strong>Institution:</strong> {institutions?.find(inst => iri.structure(inst.id) === row.institution)?.title || row.institution || '_'}</div>,
                            <div key="struct"><strong>Structure:</strong> {structuresAll?.find(s => iri.structure(s.id) === row.structure)?.title || row.structure || '_'}</div>,
                            <div key="ent"><strong>Entité:</strong> {structuresAll?.flatMap(s => s.children).find(e => iri.structure(e.id) === row.entite)?.title || row.entite || '_'}</div>,
                          ].filter(Boolean);
                          if (parts.length === 0) return "—";
                          return parts.map((p, i) => <React.Fragment key={i}>{i > 0 && " "}{p}</React.Fragment>);
                        })()}
                      </div>
                    ))) : (
                      <div>Aucune structure renseignée.</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Recap Adresse */}
              <div className="col" onClick={() => {if(actionVerb == 'add') setStep(2)}} title={(actionVerb == 'add') ? "Cliquez pour modifier les adresses": ""} style={{cursor: "pointer"}}>
                <div className="card compact-card">
                  <div className="card-body recap">
                    <div className="section-title recap-title">{localitesRepeater.length!==1 ? 'Adresses':'Adresse'}</div>
                    {localitesRepeater.filter(loc => loc.departement || loc.commune || loc.arrondissement || loc.quartier).length > 0 ? (
                      localitesRepeater?.map((loc, i) => (
                        <div key={"addr"+i} className="mb-2"> 
                          {localitesRepeater.length!==1 && <><strong>Localisation {i + 1}:</strong>{" "}<br/></>}
                          {(() => {
                            const parts = [
                              <div key="dep"><strong>Département:</strong> {departements?.find(d => iri.localite(d.id) === loc.departement)?.title || loc.departement || '_'}</div>,
                              <div key="com"><strong>Commune:</strong> {loc?.communes?.find(c => iri.localite(c.id) === loc.commune)?.title || loc.commune || '_'}</div>,
                              <div key="arr"><strong>Arrondissement:</strong> {loc?.arrondissements?.find(c => iri.localite(c.id) === loc.arrondissement)?.title || loc.arrondissement || '_'}</div>,
                              <div key="qua"><strong>Quartier/Village:</strong> {loc?.quartiers?.find(c => iri.localite(c.id) === loc.quartier)?.title || loc.quartier || '_'}</div>,
                            ].filter(Boolean);
                            if (parts.length === 0) return "—";
                            return parts.map((p, i) => <React.Fragment key={i}>{i > 0 && " "}{p}</React.Fragment>);
                          })()}
                          </div>
                      ))
                    ) : (
                      <div>Aucune localisation renseignée.</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Recap Fichiers & protections */}
              <div className="col" onClick={() => {if(actionVerb == 'add') setStep(3)}} title={(actionVerb == 'add') ? "Cliquez pour modifier les fichiers":""} style={{cursor: "pointer"}}>
                <div className="card compact-card">
                  <div className="card-body recap">
                    <div className="section-title recap-title">Fichiers & protections</div>
                    <div className="row g-2">
                      <div className="col-6">
                        <strong>Rapport</strong>
                        <div>{reportFileName || form.reportUrl || "—"}</div>
                      </div>
                      <div className="col-6">
                        <strong>Fiche de synthèse</strong>
                        <div>{abstractFileName || form.abstractUrl || "—"}</div>
                      </div>
                      <div className="col-6">
                        <strong>Annexes</strong>
                        <div>
                          {Array.isArray(annexeFileName) && annexeFileName.length ? (
                            <ul className="mb-0 pl-3">
                              {annexeFileName.map((name, i) => (
                                <li key={i}>
                                  {Array.isArray(form.annexeList) && form.annexeList[i] ? (
                                    name || form.annexeList[i]['name']
                                  ) : (
                                    name || "—"
                                  )}
                                </li>
                              ))}
                            </ul>
                          ) : Array.isArray(form.annexeList) && form.annexeList.length ? (
                            <ul className="mb-0 pl-3">
                              {form.annexeList.map((u, i) => (
                                <li key={i}>
                                    {u.name}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            "—"
                          )}
                        </div>
                      </div>
                      <div className="col-6">
                        <strong>Code</strong>
                        <div>{mask(form.code)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Recap Recommandations */}
              <div
                className="col-12"
                style={{ width: "100%", cursor: "pointer" }}
                onClick={() => {if(actionVerb == 'add') setStep(4)}}
                title={(actionVerb == 'add') ? "Cliquez pour modifier les recommandations": ""}
              >
                <div className="card compact-card">
                  <div className="card-body">
                    <div className="section-title mb-2">Recommandations</div>
                    <div>
                      {recommandations.length > 0 ? (
                        recommandations.map((r, i) => (
                          <div 
                            key={i}
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              if(actionVerb == 'add') {
                                setStep(4); setEditRecoIdx(i);
                                setRecoDraft({
                                  ...r,
                                  delaiMiseEnOeuvre: r.delaiMiseEnOeuvre ? new Date(r.delaiMiseEnOeuvre) : null
                                });
                              }
                            }}
                            className="mb-4 p-3 border rounded bg-white recap" 
                            style={{position: "relative"}}
                          >

                            {/* En-tête */}
                            <div className="d-flex align-items-center mb-3">
                              <span className="badge badge-primary mr-2" style={{ fontSize: 16, color: "black", position:"absolute", top:"0", left:"0", border: "1px solid #dee2e6" }}>
                                {String(i + 1).padStart(2, "0")}
                              </span>
                            </div>

                            {/* Lignes “titre + valeur” */}
                            <div className="row">
                              <div className="col-md-6 mb-2">
                                <span className="recap-label">
                                  Niveau de risque
                                </span>
                                <span>{r.niveauRisque || "—"}</span>
                              </div>

                              <div className="col-md-6 mb-2">
                                <span className="recap-label">
                                  Échéance
                                </span>
                                <span>{r.delaiMiseEnOeuvre ? new Date(r.delaiMiseEnOeuvre).toLocaleDateString() : "—"}</span>
                              </div>

                              <div className="col-md-6 mb-2">
                                <span className="recap-label">
                                  Responsable
                                </span>
                                <span>
                                  {typeof r.responsableMiseEnOeuvre === "object" && r.responsableMiseEnOeuvre !== null
                                    ? r.responsableMiseEnOeuvre.name // Affiche le nom si c'est un objet
                                    : users.find(i => iri.user(i.id) === r.responsableMiseEnOeuvre)?.name || r.responsableMiseEnOeuvre || "—"}
                                </span>
                              </div>

                              <div className="col-md-6 mb-2">
                                <span className="recap-label">
                                  Personne(s) incriminée(s)
                                </span>
                                <span>
                                  {Array.isArray(r.accusedPeople) && r.accusedPeople.length > 0
                                    ? r.accusedPeople.map(p => `${p.firstname} ${p.lastname}`).join(", ")
                                    : (r.personneIncriminee || "—")}
                                </span>
                              </div>

                              <div className="col-md-6 mb-2">
                                <span className="recap-label">
                                  Frais indûment perçus
                                </span>
                                <span>{r.fraisIndumentPercu || 0} FCFA</span>
                              </div>

                              <div className="col-md-6 mb-2">
                                <span className="recap-label">
                                  Suivi
                                </span>
                                <span>{r.isFollowed ? "Oui" : "Non"}</span>
                              </div>
                            </div>

                            {/* Sections riches (titre vert plein largeur + contenu) */}
                            <div className="mb-2">
                              <div className="recap-title">
                                Constat
                              </div>
                              <div dangerouslySetInnerHTML={{ __html: r.constat || "" }} />
                            </div>
                            
                            <div className="mb-2">
                              <div className="recap-title">
                                Causes
                              </div>
                              <div dangerouslySetInnerHTML={{ __html: r.cause || "" }} />
                            </div>

                            <div className="mb-2">
                              <div className="recap-title">
                                Conséquences
                              </div>
                              <div dangerouslySetInnerHTML={{ __html: r.consequences || "" }} />
                            </div>

                            <div className="mb-2">
                              <div className="recap-title">
                                Textes réglementaires
                              </div>
                              <div dangerouslySetInnerHTML={{ __html: r.textesReglementaires || "" }} />
                            </div>

                            <div className="mb-2">
                              <div className="recap-title">
                                Recommandation(s)
                              </div>
                              <div dangerouslySetInnerHTML={{ __html: r.recommandations || r.recommandations || "" }} />
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center text-muted py-4">Aucune recommandation</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </div>
          )}

          {/* Footer navigation boutons d'action */}
          {!lockToStep4 ? <div className="text-right mt-4">
            <button type="button" className="btn btn-light mx-2" onClick={back} disabled={step === 0}>
              Retour
            </button>
            {step < STEPS.length - 1 && (
              <button type="button" className="btn btn-success mx-2" onClick={next} disabled={!canNext}>
                Suivant →
              </button>
            )}
            {step === 5 &&
              <button
                type="submit"
                className="btn btn-warning mx-2"
                disabled={loading}
              >
                <Send /> {loading ? "Envoi en cours ..." : "Envoyer"}
              </button>
            }
          </div> : !justReadOnly ? <div className="text-right mt-4">

            {editRecoIdx !== null && !justReadOnly &&
              <button
                type="button"
                className="btn btn-light mx-4"
                onClick={() => { setJustReadOnly(true); startEditRecommandation(editRecoIdx); }}
                style={{ marginRight: "20px" }}
              >
                Annuler la modification
              </button>
            }

            <button
              type="button"
              className="btn btn-secondary mx-2"
              onClick={() => Swal.fire({
                title: "Vous êtes sur le point de mettre à jour ce rapport.",
                showCancelButton: true,
                confirmButtonColor: "#019264",
                confirmButtonText: "Confirmer",
                cancelButtonText: "Annuler",
              }).then((result) => {
                  if (result.isConfirmed) {                  
                    patchReco(recoDraft.id, recoDraft);
                  }
              })}
              disabled={loading}
            >
              {loading ? "Mise à jour en cours ..." : "Mettre à jour la recommandation"}
            </button>
          </div> : <div className="text-right mt-4">

            {/* Role de l'igm ou igf uniquement */}
            {actionVerb === "detail" && (UserStore.hasRole(USER_ROLES.ROLE_IGF) || UserStore.hasRole(USER_ROLES.ROLE_IGM)) && (currentRapport?.status != "transmitted_to_igf" || currentRapport?.status === "tracking_table_generated" ) && actionsSuivi.length > 0 && <>
              <button type="button" className="btn btn-secondary mx-2" onClick={() => { if (step == 5) { setStep(4); } else setSuiviOpen(true) }}>
                {step == 5 ? "Fermer" : "Tableau de suivi des recommandations"}
              </button>
              {step == 5 && actionsSuivi.length > 0 && currentId &&
                <button type="button" className="btn btn-warning" onClick={() => exportRecapPDF(`${currentId}`)}>
                  Exporter en PDF
                </button>}
            </>}
            {UserStore.hasRole(USER_ROLES.ROLE_IGF) && currentRapport?.status === "transmitted_to_igf" &&
              <button type="button" className="btn btn-success mx-2" onClick={() => Swal.fire({
                title: "Vous êtes sur le point de transférer ce rapport, cette action est irréversible.",
                showCancelButton: true,
                confirmButtonColor: "#019264",
                confirmButtonText: "Confirmer",
                cancelButtonText: "Annuler",
              }).then((result) => {
                if (result.isConfirmed) {
                  updateRepport('return-for-revision');
                }
              })}>
                <Send /> Retourner pour révision
              </button>
            }
            {/* Role de l'igm uniquement */}
            {UserStore.hasRole(USER_ROLES.ROLE_IGM) && currentRapport?.status != "transmitted_to_igf" && (currentRapport?.status == "submitted" || currentRapport?.status === "transmitted_to_igm") &&
              <button type="button" className="btn btn-success mx-2" onClick={() => Swal.fire({
                title: "Vous êtes sur le point de transférer ce rapport, cette action est irréversible.",
                showCancelButton: true,
                confirmButtonColor: "#019264",
                confirmButtonText: "Confirmer",
                cancelButtonText: "Annuler",
              }).then((result) => {
                if (result.isConfirmed) {
                  updateRepport(currentRapport?.status === 'transmitted_to_igm' ? 'return-to-validate-recommendations' : 'transmit-to-igf' );
                }
              })}>
                <Send /> Transférer toutes les recommandations
              </button>
            }
            {UserStore.hasRole(USER_ROLES.ROLE_IGM) && currentRapport?.status != "transmitted_to_igf" && (currentRapport?.status === "transmitted_to_igm") &&
              <button type="button" className="btn btn-warning mx-2" onClick={() => Swal.fire({
                title: "Vous êtes sur le point d'envoyer les actions de suivi de ce rapport.",
                showCancelButton: true,
                confirmButtonColor: "#019264",
                confirmButtonText: "Confirmer",
                cancelButtonText: "Annuler",
              }).then((result) => {
                if (result.isConfirmed) {
                  updateRepport('define-action-to-implement');
                }
              })}>
                Envoyer les actions pour validation
              </button>
            }
            {/* role de l'igf uniquement */}
            {UserStore.hasRole(USER_ROLES.ROLE_IGF) && <>
              {(currentRapport?.status === "transmitted_to_igf" || currentRapport?.status === "to_ravalidate_by_igf") ?
              <button type="button" className="btn btn-warning mx-2" onClick={() => Swal.fire({
                title: "Vous êtes sur le point de valider les recommandations de ce rapport, cette action est peut-être irréversible.",
                showCancelButton: true,
                confirmButtonColor: "#019264",
                confirmButtonText: "Confirmer",
                cancelButtonText: "Annuler",
              }).then((result) => {
                if (result.isConfirmed) {
                  updateRepport('validate-recommendations');
                }
              })}>
                Valider les recommandations
              </button>
              : currentRapport?.status === "tracking_table_generated" ?
              <button type="button" className="btn btn-warning mx-2" onClick={() => Swal.fire({
                title: "Vous êtes sur le point de valider ce rapport, cette action est irréversible.",
                showCancelButton: true,
                confirmButtonColor: "#019264",
                confirmButtonText: "Confirmer",
                cancelButtonText: "Annuler",
              }).then((result) => {
                if (result.isConfirmed) {
                  updateRepport('complete');
                }
              })}>
                Valider le rapport complet
              </button> 
              : currentRapport?.status === "action_to_implement_defined" ?
              <button type="button" className="btn btn-warning mx-2" onClick={() => {
                let sendMessage = "";
                if(form.authorizedRecipients?.length > 0) sendMessage = "Il est partagé avec : " + form.authorizedRecipients?.map(u => u.name).join(", ");
                Swal.fire({
                  title: "Vous êtes sur le point de valider le tableau de suivi pour valider ce rapport. "+sendMessage,
                  showCancelButton: true,
                  confirmButtonColor: "#019264",
                  confirmButtonText: "Confirmer",
                  cancelButtonText: "Annuler",
                }).then((result) => {
                  if (result.isConfirmed) {
                    updateRepport('generate-tracking-table');
                  }
                })}
              }>
                Valider le suivi du rapport
              </button>
              :
              ''
              }
            </>}
          </div>}
          {/* Fin Footer navigation boutons d'action */}
        </form>

        {/* Modal personne incriminée */}
        <Modal
          show={modalOpen}
          large={false}
          size="modal-md"
          title="Ajouter une personne incriminée"
          body={<>
              <form>
                <input
                  className="form-control mb-2"
                  placeholder="Nom *"
                  id="personLastname"
                  value={personSearch.lastname}
                  onChange={e => handlePersonSearch("lastname", e.target.value)}
                />
                <input
                  className="form-control mb-2"
                  placeholder="Prénom(s) *"
                  id="personFirstname"
                  value={personSearch.firstname}
                  onChange={e => handlePersonSearch("firstname", e.target.value)}
                />
                <input
                  className="form-control mb-2"
                  placeholder="Email"
                  id="personEmail"
                  value={personSearch.email}
                  onChange={e => handlePersonSearch("email", e.target.value)}
                  type="email"
                />
                
                {personResults.length > 0 && (
                  <ul className="list-group mb-2">
                    {personResults.map(p => (
                      <li key={p.id} className="list-group-item d-flex justify-content-between align-items-center">
                        <span>{p.firstname} {p.lastname} ({p.email})</span>
                        <button type="button" className="btn btn-sm btn-secondary" onClick={() => handleAddPerson(p)}>
                          Sélectionner
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                <button type="button" className="btn btn-secondary" onClick={closePersonModal}>Annuler</button>
                <button type="button" className="btn m-3 btn-success" onClick={() => handleAddPerson(personSearch, true)} disabled={loadingAddPerson}>
                  Ajouter
                </button>
              </form>
            </>}
          onClose={() => {setLoadingAddPerson(false);closePersonModal();}}
        />

        {/* Modal commentaire */}
        <Modal
          show={commentOpen??false}
          large={false}
          size="modal-md"
          title="Ajouter un commentaire"
          body={<>
              <span>
                <div className="form-group my-3">
                  <UploadPdfDropzone
                    label="Ajouter un fichier"
                    hint="Formats acceptés PDF (max. 200MB)"
                    maxMB={200}
                    multiple={false}
                    value={commentDraft?.file ? { id: commentDraft.fileId, name: commentDraft.fileName } : null}
                    apiBase={apiBase}
                    authHeaders={authHeaders}
                    onUploaded={(fileOrNull) => {
                      if (!fileOrNull) {
                        setCommentDraft((p) => ({ ...p, file: "", fileId: "", fileName: "" }));
                        return;
                      }
                      setCommentDraft((p) => ({
                        ...p,
                        file: `/api/media/${fileOrNull.id}`.replace("//api", "/api"),
                        fileId: fileOrNull.id,
                        fileName: fileOrNull.name || "document.pdf",
                      }));
                    }}
                  />
                </div>

                <div className="form-group mt-3" id="comment-modal-body">
                  <label className="mb-2 label">Commentaire *</label>
                  <Quill
                    theme="snow"
                    bounds="#comment-modal-body"
                    scrollingContainer="#comment-modal-body"
                    modules={quillModules}
                    value={commentDraft?.content || ""}
                    onChange={(val) => {setCommentDraft((p) => ({ ...p, content: val }))}}
                  />
                </div>
                <div className="modal-footer text-center">
                  <button type="button" className="btn btn-secondary" onClick={closeCommentModal}>
                    Annuler
                  </button>
                  <button
                    type="button"
                    className="btn btn-success"
                    disabled={!canSaveComment || commentSaving}
                    onClick={() => saveComment?.(commentDraft)}
                  >
                    {commentSaving ? "Envoi..." : "Enregistrer"}
                  </button>
                </div>
              </span>
            </>}
          onClose={closeCommentModal}
        />

        {/* Modal formulaire de suivi */}
        <Modal
          show={suiviModalOpen??false}
          large={false}
          size="modal-md"
          title="Action de suivi de recommandation"
          body={
                  <div className="row">
                  {UserStore.hasRole(USER_ROLES.ROLE_IGM) && justReadOnly &&
                    <div className="bg-light p-3 mt-2">
                      <div className="row">
                        <div className="col-md-6 mb-2">
                          <span className="label">Niveau de mise en oeuvre *</span>
                          <select
                            name="etat"
                            // disabled={justReadOnly}
                            className="form-control"
                            value={suiviData?.etat??''}
                            onChange={(e) => setSuiviData({ ...suiviData, etat: e.target.value })}
                          >
                            <option value="">Choisir…</option>
                            <option value="Pas démarré">Pas démarré</option>
                            <option value="Démarré">Démarré</option>
                            <option value="Avancé">Avancé</option>
                            <option value="Finalisé">Finalisé</option>
                          </select>
                        </div>
                        <div className="col-md-6 mb-2">
                          <span className="label">Périodicité *</span>
                          <div className="d-flex">
                          <input
                            className="form-control"
                            type="number"
                            style={{width: '20%', display: 'inline-block', marginRight: '10px'}}
                            value={suiviData?.periodicite || ""}
                            onChange={(e) => setSuiviData({ ...suiviData, periodicite: e.target.value })}
                          />
                          <select
                            name="periodiciteUnit"
                            // disabled={justReadOnly}
                            style={{width: '80%', display: 'inline-block'}}
                            className="form-control"
                            value={suiviData?.periodiciteUnit??''}
                            onChange={(e) => setSuiviData({ ...suiviData, periodiciteUnit: e.target.value })}
                          >
                            {/* <option value="années">années</option> */}
                            <option value="mois">mois</option>
                            <option value="jours">jours</option>
                            <option value="heures">heures</option>
                          </select>
                          </div>
                        </div>
                        <div className="col-md-12 mb-2">
                          <span className="label">Actions de mise en oeuvre</span>
                          <Quill
                            theme="snow"
                            key={`action-${formResetKey}`}
                            title="Actions de mise en oeuvre"
                            value={suiviData?.actions_mises_en_oeuvre||""}
                            onChange={val => setSuiviData({ ...suiviData, actions_mises_en_oeuvre: val })}
                          />
                        </div>
                        <div className="col-md-1 mt-3">
                          <button
                            type="button"
                            className="btn btn-secondary mx-2"
                            onClick={()=>postSuivi(recoDraft.id)}
                            disabled={loading}
                          >
                            {loading ? "..." : "Sauvegarder"}
                          </button>
                        </div>
                      </div>
                    </div>
                  }
                  </div>
            }
          onClose={()=>setSuiviModalOpen(false)}
        /> 

        {/* Modal tableau de suivi, IGF uniquement */}
        {(UserStore.hasRole(USER_ROLES.ROLE_IGF) || UserStore.hasRole(USER_ROLES.ROLE_IGM)) &&
        <Modal
          show={suiviOpen??false}
          large={true}
          size="modal-md"
          title="Table de suivi des recommandations"
          body={<>
              <span>
                <table className="table table-striped">
                  <thead style={{backgroundColor: '#019264', color: 'white'}}>
                    <tr>
                      <th>#</th>
                      <th>État</th>
                      <th>Périodicité</th>
                      <th>Actions mises en oeuvre</th>
                    </tr>
                  </thead>
                  <tbody>
                    {actionsSuivi?.length > 0 ? actionsSuivi.map((r, i) => (
                      <tr key={i}>
                        <td>{String(i + 1).padStart(2, "0")}</td>
                        <td>{r.etat || "—"}</td>
                        <td>{r.periodicite || "—"}</td>
                        <td><div dangerouslySetInnerHTML={{ __html: r.actions_mises_en_oeuvre || "" }} /></td>
                      </tr>
                    )) :  
                      <tr>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td>
                        <div style={{fontWeight: 'bold'}}>Aucun enregistrement disponible.</div>
                      </td>
                    </tr>}
                  </tbody>
                </table>
                <div className="modal-footer text-center">
                  <button type="button" className="btn btn-secondary" onClick={()=>setSuiviOpen(false)}>
                    Fermer
                  </button>
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={() => {setStep(5); setSuiviOpen(false);}}
                  >
                    Fiche de suivi des recommandations
                  </button>
                </div>
              </span>
            </>}
          onClose={()=>setSuiviOpen(false)}
        />}

        <Modal
          large
          size="modal-lg"
          show={showViewer && !!viewerSource}
          title="Lecture du Document"
          body={<SecureFileViewer source={viewerSource} height={700} />}
          onClose={() => { setShowViewer(false); setViewerSource(null); }}
        />

        {/* Tableau des lignes déjà ajoutées */}
        {recommandations.length > 0 && step === 4 && <div className="section-title mt-4">
          {/* seach barre */}
          <div className="d-flex justify-content-between align-items-center mb-2">
              <input
                type="text"
                name="searchRecommandation"
                id="searchRecommandation"
                className="form-control"
                style={{ width: '100%' }}
                disabled
                placeholder="Rechercher dans les recommandations ajoutées..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setRecommandations(prevRecommandations => {
                    return prevRecommandations.filter(r => 
                      r.constat.toLowerCase().includes(e.target.value.toLowerCase()) ||
                      (r.recommandations && r.recommandations.toLowerCase().includes(e.target.value.toLowerCase()))
                    );
                  });
                }}
              />
          </div>
          {/* [SELECTION] Barre d’actions */}
          {/* <div className="d-flex justify-content-between align-items-center mb-2">
            <div>
              {selected.size > 0 && (
                <span className="text-muted">{selected.size} élément(s) sélectionné(s)</span>
              )}
            </div>
            {recommandations.length > 0 && selected.size > 0 && <div>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={removeSelected}
                disabled={selected.size === 0}
                title="Supprimer toutes les lignes sélectionnées"
              >
                <Trash /> Supprimer la sélection
              </button>
            </div>}
          </div> */}
          <table className="table table-striped">
            <thead style={{backgroundColor: '#019264', color: 'white'}}>
              <tr>
                {/* [SELECTION] colonne checkbox */}
                <th style={{ width: 36 }}>
                  {/* {recommandations.length > 0 && <input
                    ref={selectAllRef}
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    aria-label="Tout sélectionner"
                  />} */}
                </th>
                <th>#</th>
                <th>Constat</th>
                <th>Niveau de risque</th>
                <th>Échéance</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {recommandations?.map((r, i) => (
                <tr key={i}>
                  {/* [SELECTION] case par ligne */}
                  <td>
                    {actionVerb == 'edit' || actionVerb == 'detail' && <input
                      disabled
                      type="checkbox"
                      checked={i === editRecoIdx || selected.has(i)}
                      onChange={() => toggleOne(i)}
                      aria-label={`Sélectionner la ligne ${i + 1}`}
                    /> }
                  </td>
                  <td style={{color: r.isFollowed ? "red": "black"}}>{String(i + 1).padStart(2, "0")}</td>
                  <td className="text-truncate" style={{maxWidth: 320, color: r.isFollowed ? "red": "black"}}>{stripAndTruncate(r.constat)}</td>
                  <td style={{color: r.isFollowed ? "red": "black"}}>{r.niveauRisque || "—"}</td>
                  <td style={{color: r.isFollowed ? "red": "black"}}>{r.delaiMiseEnOeuvre ? new Date(r.delaiMiseEnOeuvre).toLocaleDateString() : "—"}</td>
                  <td>
                    {justReadOnly && (actionVerb == 'view' || actionVerb == 'read') ? // || UserStore.hasRole(USER_ROLES.ROLE_INSPECTEUR) ? 
                      <button
                        type="button"
                        className="btn btn-sm mr-2"
                        style={{marginRight: '10px', boxShadow: "none"}}
                        onClick={() => {startEditRecommandation(i); setJustReadOnly(true)}}
                      >
                        <Eye />
                      </button>
                      :
                      <>
                        {/* IGF uniquement */}
                        { UserStore.hasRole(USER_ROLES.ROLE_IGF) && <span title="Suivi requis" style={{marginRight: '10px', position: "relative", top:"7px"}}>
                          <Toggle
                            checked={!!r.isFollowed}
                            disabled={(currentRapport?.status !== "transmitted_to_igf") ^ (currentRapport?.status !== "to_ravalidate_by_igf") ^ justReadOnly}
                            onChange={() => {
                              setEditRecoIdx(i);
                              if(!!r.isFollowed){
                                Swal.fire({
                                  title: "Êtes-vous sûr de vouloir désactiver le suivi de cette recommandation ?",
                                  showCancelButton: true,
                                  confirmButtonColor: "#019264",
                                  confirmButtonText: "Confirmer",
                                  cancelButtonText: "Annuler",
                                }).then((result) => {
                                  if (result.isConfirmed) {
                                    setRecoDraft({ ...recoDraft, isFollowed: !r.isFollowed });
                                    patchReco(r.id, false);
                                  }
                                });
                                return;
                              }
                              setRecoDraft({ ...recoDraft, isFollowed: r.isFollowed });
                              patchReco(r.id);
                            }}
                          />
                        </span>}

                        {/* IGF et IGM uniquement */}
                        {justReadOnly && actionVerb == 'detail' && (UserStore.hasRole(USER_ROLES.ROLE_IGF) || UserStore.hasRole(USER_ROLES.ROLE_IGM)) && <button
                          title="Laisser un commentaire"
                          type="button"
                          className="btn btn-sm"
                          disabled={currentRapport?.status === "completed" }
                          onClick={()=> {startEditRecommandation(i); setJustReadOnly(true); openCommentModal(r.id)}}
                          style={{marginRight: '8px', marginLeft: "5px", padding: "0px 5px", boxShadow: "none"}}
                        >
                          <i className="fa fa-commenting-o" aria-hidden="true" style={{padding: "0px"}}></i>
                        </button>}
                        
                        {actionVerb !== 'add' &&
                        <button
                          type="button"
                          title="Visualiser"
                          className="btn btn-sm mr-2"
                          style={{marginRight: '10px', boxShadow: "none"}}
                          onClick={() => {startEditRecommandation(i); setJustReadOnly(true)}}
                        >
                          <Eye />
                        </button>}
                        
                        {/*  inspecteur uniquement peut modifier lors de l'ajout */}
                        {UserStore.hasRole(USER_ROLES.ROLE_INSPECTEUR) && actionVerb == "add" && 
                        <button
                          type="button"
                          title="Modifier"
                          className="btn btn-sm mr-2"
                          style={{marginRight: '10px', boxShadow: "none"}}
                          onClick={() => {startEditRecommandation(i); setJustReadOnly(false)}}
                        >
                          <Pencil />
                        </button>}
                        
                        {/* IGM uniquement peuvent modifier à tout moment */}
                        {UserStore.hasRole(USER_ROLES.ROLE_IGM) && 
                        <button
                          type="button"
                          title="Modifier"
                          disabled={currentRapport?.status === "completed" }
                          className="btn btn-sm mr-2"
                          style={{marginRight: '10px', boxShadow: "none"}}
                          onClick={() => {startEditRecommandation(i); setJustReadOnly(false)}}
                        >
                          <Pencil />
                        </button>}
                        
                        {/* IGM uniquement */}
                        {UserStore.hasRole(USER_ROLES.ROLE_IGM) && 
                        <button
                          type="button"
                          disabled={currentRapport?.status !== "transmitted_to_igm" }
                          title="Actions de suivi"
                          className="btn btn-sm mr-2"
                          style={{marginRight: '10px', boxShadow: "none", color: actionsSuivi.filter(a => idFromIri(a.recommandation) === idFromIri(r.id)).length > 0 ? 'red' : 'black'}}
                          onClick={() => {startEditRecommandation(i); setSuiviModalOpen(true)}}
                        >
                          <PlusCircle />
                        </button>
                        }
                        {UserStore.hasRole(USER_ROLES.ROLE_INSPECTEUR) && actionVerb == 'add' && 
                        <button title="Supprimer" type="button" className="btn btn-sm" style={{boxShadow: "none"}} onClick={() => confirmAndRemoveRecommandation(i)}>
                          <Trash />
                        </button> }
                      </>}
                  </td>
                </tr>
              ))}
              {recommandations.length === 0 && (
                <tr>
                  {/* +1 colonne pour la case à cocher => colSpan passe à 6 */}
                  <td colSpan={6} className="text-center">
                    Aucune recommandation
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        }

        {/* Aperçu JSON (dev) */}
        {/* <div className="row mt-4">
          <div className="col-12">
            <pre className="p-3 bg-light" style={{ whiteSpace: "pre-wrap" }}>
              {JSON.stringify(payloadPreview, null, 2)}
            </pre>
          </div>
        </div> */}
      </div>
      {currentRapport?.rapportWorkflowActivities?.length !== 0 && actionVerb != 'read' && <div className="col-lg-3 mt-3 mt-lg-0" style={{paddingRight: '0'}}>
        <WorkflowTimeline activities={currentRapport?.rapportWorkflowActivities || []} comments={currentRapport?.allComments||[]} onRead={(r) => readDoc(r)} />
      </div>}
      </div>
    </div>
  );
}

/** ✅ Exporter un composant, pas un élément JSX */
const Page = (props) => <Layout body={<SendRapportSuivi {...props} />} />;

export default Page;
